/**
 * Settlement renderer — terrain-conforming geometry (roads, retaining walls,
 * stairs) for the W2b grown regions.
 *
 * Unlike the retired flat-pad settlements, whose basis could be shared across
 * quaternion carry layout (u, v) into a local render frame, a SETTLEMENT hugs
 * the wild terrain: its roads ripple over the ground, its walls stand where the
 * cut is deep, its stairs step down real slopes. So the geometry is generated
 * DIRECTLY in planet-local 3D via the region chart's `localToDir` (chart u,v →
 * unit dir) and the sampler's terrain height — NO tangent remap, NO per-town
 * quaternion. The meshes drop straight into `planet.group` (planet-local, like
 * the terrain patches), so they ride the planet's spin and orbit for free.
 *
 * The two builders below are PURE (no THREE, raw typed arrays) so they can be
 * oracle-tested in node: the class wraps them into merged/instanced meshes with a
 * shared-kit geometry + one-build-per-frame BUILD/DISPOSE hysteresis.
 *
 * SHARED-CODE DISCIPLINE: the pure builders read only public shared APIs
 * (localToDir, sampler.height/sample, index.carveAt) — they change no shared file.
 */
import {frontierBuildingsView} from './frontierBuildingsView';
import * as THREE from "three";
import {buildRegionOutdoors,outdoorPlanetFrame} from '../../../../shared/src/worldgen/outdoors';
import {buildOutdoorsView} from './outdoorsView';
import {regionCivicBuildings} from '../../../../shared/src/worldgen/civics';
import {civicBuildingsView} from './civicsView';
import { basinGeometry, buildTownFountain, makePoolWaterMat } from "./fountain";
import {
  type CityGraph,
  type PlanetSampler,
  type SettlementIndex,
  type CellId,
  type CarveHit,
  type TerrainSample,
  type RegionFrame,
  localToDir,
  dirToLocal,
  regionFrame,
  dirToCell,
  edgeNeighbor,
  packCell,
  NODE_PORT,
  buildStructures,
  type StructureRejection,
  buildSettlementTrees,
  buildStreetlights,
  STREETLIGHT_STRIDE,
  STREETLIGHT_HEIGHT_M,
  BUILDING_STRIDE,
  LEVEL_REGION,
  SIDE_NEG_U,
  SIDE_POS_U,
  SIDE_NEG_V,
  SIDE_POS_V,
  ROAD_W,
  STAIR_RISE_M,
  EDGE_ARTERIAL,
  EDGE_STREET,
  EDGE_STAIR,
  EDGE_TUNNEL,
  EDGE_TERRACE,
  EDGE_BRIDGE,
  mulberry32,
  districtAt, NODE_SEED, hashSeed, hashInts, h01,
  isParkParcel, USE_MARKET_SQ, USE_WELL, USE_MONUMENT, USE_LANDING_PAD, pointInPolygon,
  collapseRegion, indexRegionRoster, regionQuestRoster, regionSettlements,
  TIER_PARAMS,
  type NpcDef, type RegionSettlement,
} from "@hyperlight/shared";
import { addStylizedShading } from "../stylizedShading";
import type { Planet } from "../planet";
import type { StarSystem } from "../starsystem";
import { bakeBuildingMeshes, buildingDoors, buildingInstances, type DoorPlacement } from "./buildingMeshes";
import {
  bakeStreetlights,
  streetlightUnitGeoms,
  nearestLamps,
  STREETLIGHT_REAL_MAX,
  STREETLIGHT_LIGHT_RANGE_M,
} from "./streetlightMeshes";

// ── tuning (exported so the oracle derives its count relations from the source) ─

/** Along-edge road-ribbon sample cadence, metres (a vertex row every ~6 m). */
export const ROAD_SAMPLE_M = 6;
/** Road ribbon FULL width (metres) indexed by EDGE_* kind; TUNNEL slot unused. */
export const ROAD_WIDTH_BY_KIND = [
  ROAD_W.arterial, //     EDGE_ARTERIAL (0)
  ROAD_W.street, //       EDGE_STREET   (1)
  ROAD_W.stair, //        EDGE_STAIR    (2)
  ROAD_W.tunnel, //       EDGE_TUNNEL   (3) — excluded, kept for index alignment
  ROAD_W.terracePath, //  EDGE_TERRACE  (4)
  ROAD_W.bridge, //       EDGE_BRIDGE   (5)
] as const;
/**
 * Resolved ribbon width of edge `e` (v11): the graph's per-edge width channel
 * (class × tier × era, resolved at generation) with the kind table as the
 * fallback for width-0 test graphs. THE width every draw path uses.
 */
function edgeWidthOf(g: CityGraph, e: number): number {
  const w = g.edgeWidth?.[e] ?? 0; // hand-built test graphs may omit the channel
  return w > 0 ? w : ROAD_WIDTH_BY_KIND[g.edgeKind[e]];
}
/** Vertical lift so the ribbon reads ABOVE the terrain it drapes, metres. */
const ROAD_LIFT_M = 0.12;
/**
 * Minimum clearance (metres) a BRIDGE deck keeps above whatever it crosses (D4(e)).
 * The old plank interpolated only its endpoint heights, so an overpass at a similar
 * grade to the crossed road z-fought / merged into it ("unresolved crossing"). The
 * deck now samples the terrain under its span and lifts to clear the highest point
 * by this margin — so a bridge reads as an ELEVATED crossing, height-DEPENDENT on
 * what it spans, not a flat plank. On a normal valley/river span the endpoint chord
 * is already above the dip, so the clearance is inactive and the deck is the plain span.
 */
const BRIDGE_CLEARANCE_M = 1.5;
/** Side-stringer (rail) cross-section thickness of a stair ramp, metres. */
const STAIR_STRINGER_W_M = 0.35;
/** Stair APRON skirt bottom drop below the sampled ground, metres (seals the side). */
const STAIR_APRON_DROP_M = 0.5;
/**
 * Ground-conform threshold: a ribbon ROW whose centre carve `k` is at or above this
 * keeps the flat-across height (the carved bed is guaranteed flat there, so the
 * output stays BYTE-IDENTICAL to the pre-conform build). Below it the carve is
 * clamped/eased (a cliff — bed NOT guaranteed) and each edge vertex conforms to the
 * ground at its own position, blended toward flat-across by k. See buildRoadStrips.
 */
const RIBBON_CONFORM_K = 0.95;
/**
 * On a clamped row the edge height blends toward the flat centreline by k, but the
 * lift OFF its own ground is CAPPED to this many metres so a steep cross-slope near
 * the k=0.95 boundary can never float the edge past the ground tolerance (the
 * ribbon-never-floats guarantee). For gentle cross-slopes the cap is inactive and
 * the height is the plain k-blend `k·centreline + (1−k)·own-ground`.
 */
const RIBBON_CONFORM_CAP_M = 0.25;

// D4(d): the BUILD gate is now RESIDENCY-based (build the resident 3×3 ring), NOT a
// fixed metric radius — a fixed BUILD_DIST mismatched the topological ring and left
// resident (visible) neighbour cells' roads unbuilt. DISPOSE stays a metric-radius
// hysteresis: a cell far enough to tear down is well outside the resident ring, so
// it can never dispose a still-resident cell (dispose ≫ any region-cell diagonal in
// the ring at the altitudes residency engages).
const DISPOSE_DIST = 8000; // tear down beyond this (hysteresis)

/**
 * The BUILD-GATE predicate, extracted PURE so it is oracle-testable (D4(d)): a region
 * cell is eligible to build iff its region is RESIDENT (`isResident(rk)` true) — the
 * deleted `BUILD_DIST` metric radius plays NO part. This is the exact test `update()`
 * applies (`!svc.index.has(rk) → continue`), lifted to a function so the oracle asserts
 * REAL behaviour: a resident ring member gates IN, a non-resident cell gates OUT, and no
 * distance can resurrect the removed metric gate. `isResident` is the caller's residency
 * probe (in production `svc.index.has`); passing it in keeps this free of the live index.
 */
export function settlementBuildGate(rk: number, isResident: (rk: number) => boolean): boolean {
  return isResident(rk);
}

// ── plain-number vec helpers (shared by the pure builders AND the renderer, so
//    the stair yaw the builder encodes and the frame the renderer rebuilds agree) ─

/**
 * A right-handed tangent frame at planet-local point (px,py,pz): `up` is the
 * outward radial, `t1`/`t2` span the tangent plane. Deterministic from the point
 * alone (the reference-axis pick is stable), so a yaw measured against (t1,t2)
 * here is reconstructible by the renderer from the same point — the contract the
 * stair boxes rely on ([x,y,z,yaw,…] carries orientation in exactly this frame).
 */
function tangentFrame(px: number, py: number, pz: number): { up: number[]; t1: number[]; t2: number[] } {
  const l = Math.hypot(px, py, pz) || 1;
  const up = [px / l, py / l, pz / l];
  // Reference axis least aligned with `up`, so the cross product is well-conditioned.
  const rx = Math.abs(up[1]) < 0.9 ? 0 : 1;
  const ry = Math.abs(up[1]) < 0.9 ? 1 : 0;
  // t1 = normalize(ref × up)
  let t1x = ry * up[2] - 0 * up[1];
  let t1y = 0 * up[0] - rx * up[2];
  let t1z = rx * up[1] - ry * up[0];
  const l1 = Math.hypot(t1x, t1y, t1z) || 1;
  t1x /= l1; t1y /= l1; t1z /= l1;
  // t2 = up × t1
  const t2x = up[1] * t1z - up[2] * t1y;
  const t2y = up[2] * t1x - up[0] * t1z;
  const t2z = up[0] * t1y - up[1] * t1x;
  return { up, t1: [t1x, t1y, t1z], t2: [t2x, t2y, t2z] };
}

// ── road-display smoothing: chain → centripetal Catmull-Rom → re-tessellate ────
//
// The user's "roads are essentially straight lines" finding is a DISPLAY problem:
// arterials are traced as a polyline of short same-kind edges through degree-2
// junction nodes (growth.ts), so each edge draws as a straight chord and the
// meander only kinks at the nodes. The fix chains those edges back into polylines
// and runs a centripetal Catmull-Rom curve through the node points, so the ribbon
// reads as one smooth road. It is a render-path transform ONLY: the graph and the
// carve are untouched, so the smoothed centreline is CLAMPED to stay within
// ROAD_W/4 of the original chord — the carve flattens a bed of that half-order, so
// the ribbon must not curve out of its carved bed.
//
// Kinds that chain: ARTERIAL / STREET / TERRACE (terrain-following, non-stair).
// STAIR is EXCLUDED (Task 3 renders it as a stepped ramp and the renderer filters
// STAIR quads out of the road mesh — smoothing must leave those straight so the
// ramp ends stay flush). BRIDGE stays a straight plank. TUNNEL is never drawn.
//
// R1 D4(b) DE-STARVE: the old gate broke a chain at ANY node that was not exactly
// degree-2 same-kind, so every T-junction / 3-way / kind change left a straight
// stub. The walker below instead continues THROUGH a junction along the BEST-
// ALIGNED continuation edge (smallest heading change, within CONTINUE_MAX_TURN)
// and ACROSS an aligned kind change (primary→street), so primaries curve through
// intersections. The pass is still a strict PARTITION — every eligible non-
// degenerate edge lands in exactly one chain — so the render count relations hold.

/** Lateral-deviation cap as a fraction of the FULL road width (ROAD_W/4). */
const LATERAL_CAP_FRAC = 0.25;

// ── junction FLARES (W2d-3 wrap — replaces the R1 disc fillets) ───────────────
// A road that TERMINATES at a junction opens a trumpet MOUTH toward the roads it
// meets: over the last FLARE zone its half-width eases quadratically from its own
// half-width up to own + FLARE_FRAC × (the widest OTHER incident road's half-width),
// with rows densified so the curve reads smooth. The mouth is part of the ribbon
// itself — same geometry, same gravel UV flow, same tint — so the join has NO
// separate patch to clip or z-fight (the disc fillets were a second coplanar mesh:
// they flickered against the ribbons and their rims overhung uncarved grass).
/** Mouth extra half-width as a fraction of the crossing road's half-width. */
const FLARE_FRAC = 0.9;
/** Flare zone length = this × (own half-width + mouth extra), min 4 m. */
const FLARE_LEN_K = 1.8;
/** Row spacing inside a flare zone (m) — the trumpet curve's resolution. */
const FLARE_ROW_M = 1.6;
/** Deterministic depth order at overlaps: lift = ROAD_LIFT_M + width × this, so a
 *  WIDER road always renders over a narrower one where their ribbons overlap at a
 *  junction — a small path visibly merges INTO the big road, and no coplanar
 *  z-fight shimmer remains. ≤ ~11 cm at metro trunk width — invisible in-world. */
const LIFT_WIDTH_K = 0.006;
/** Equal-width overlap tie-break: + (chain ordinal % 5) × this. */
const LIFT_JITTER_M = 0.0035;
/**
 * A junction continuation is only chained when the incoming→outgoing heading turn
 * is at most this (radians ≈ 75°). Beyond it the two arms read as a genuine corner,
 * not one flowing road, so the chain terminates and both arms curve independently.
 */
const CONTINUE_MAX_TURN = 1.309; // 75°
/**
 * Border ports within this distance (region-local metres) of a neighbour cell's
 * port are treated as the SAME road crossing the border, so a chain ending at a
 * home port continues into the neighbour graph (D4(c) cross-border stitching). The
 * hashed-port machinery lands matching ports ≤1 m apart; 3 m tolerates chart drift.
 */
const BORDER_PORT_MATCH_M = 3;

/** A run of edges chained through junctions (node indices, into the region graph).
 *  `width` = the FIRST edge's resolved v11 width (chains break on width change,
 *  so it holds for every member). `firstEdge`/`lastEdge` are the terminal edge
 *  ids — the junction-flare pass excludes a chain's own edge when sizing the
 *  mouth it opens toward the OTHER roads at its end node. */
export interface RoadChain { kind: number; width: number; nodes: number[]; firstEdge: number; lastEdge: number }

/** A chained polyline whose control points are already resolved to home-chart (u,v)
 *  — the cross-border form, since a stitched chain spans two cells' charts.
 *  `endA`/`endB` are the chain's terminal NODE ids in the home graph (−1 when the
 *  tail was grafted into a neighbour cell — a continuing road, never flared). */
export interface RoadChainCP {
  kind: number; width: number; u: number[]; v: number[];
  endA: number; endB: number; edgeA0: number; edgeB0: number;
}

const chainEligible = (k: number): boolean => k === EDGE_ARTERIAL || k === EDGE_STREET || k === EDGE_TERRACE;

/** Unit heading of edge `e` LEAVING node `from` (region-local u,v), or null if degenerate. */
function edgeHeading(g: CityGraph, e: number, from: number): [number, number] | null {
  const a = g.edgeA[e], b = g.edgeB[e];
  const to = a === from ? b : a;
  const du = g.nodeU[to] - g.nodeU[from], dv = g.nodeV[to] - g.nodeV[from];
  const l = Math.hypot(du, dv);
  if (l < 1e-6) return null;
  return [du / l, dv / l];
}

/**
 * Chain a region's ARTERIAL/STREET/TERRACE edges into maximal smoothing polylines,
 * continuing THROUGH junctions on the best-aligned continuation and across aligned
 * kind changes (D4(b)). Returns node-index chains (the region-local form).
 *
 * WALK: at each interior node the chain, arriving on edge `e`, picks the unvisited
 * eligible incident edge whose heading best CONTINUES the arrival heading (max dot,
 * i.e. smallest turn) provided the turn ≤ CONTINUE_MAX_TURN; ties break to the
 * lowest edge index (determinism). A node with no such continuation terminates the
 * chain. Kind is inherited from the FIRST edge; a kind change mid-chain is allowed
 * when aligned (the emitted ribbon width follows the chain's first kind — acceptable
 * since primary↔street widths are close and the carve bed already unions them).
 *
 * PARTITION: `visited` guards every edge, and a walk claims an edge exactly once, so
 * every eligible non-degenerate edge lands in exactly one chain (the render count
 * relation + the F2 oracle depend on this). Deterministic: incident lists ascend by
 * edge index; seeds scanned in ascending node then edge order; cycles by edge order.
 */
export function smoothChains(g: CityGraph): { chains: RoadChain[] } {
  const nNodes = g.nodeU.length;
  const nEdges = g.edgeKind.length;
  const inc: number[][] = Array.from({ length: nNodes }, () => []);
  const degenerate = new Uint8Array(nEdges);
  for (let e = 0; e < nEdges; e++) {
    const a = g.edgeA[e], b = g.edgeB[e];
    if (Math.hypot(g.nodeU[b] - g.nodeU[a], g.nodeV[b] - g.nodeV[a]) < 1e-6) { degenerate[e] = 1; continue; }
    inc[a].push(e); inc[b].push(e);
  }
  const other = (e: number, node: number): number => (g.edgeA[e] === node ? g.edgeB[e] : g.edgeA[e]);
  const visited = new Uint8Array(nEdges);

  /**
   * The best UNVISITED eligible continuation of arrival-edge `arr` at `node`: the
   * incident edge whose leaving heading best matches the arrival heading through the
   * node, turn ≤ CONTINUE_MAX_TURN. Returns -1 when the chain must terminate. When
   * `arr` is -1 (chain start) there is no arrival heading, so it is never called for
   * the seed edge — the seed is chosen explicitly by the outer loop.
   */
  const bestContinuation = (node: number, arr: number): number => {
    const arrH = edgeHeading(g, arr, other(arr, node)); // heading INTO the node
    if (arrH === null) return -1;
    let best = -1, bestDot = Math.cos(CONTINUE_MAX_TURN) - 1e-9;
    const arrW = edgeWidthOf(g, arr);
    for (const e of inc[node]) {
      if (e === arr || visited[e] || degenerate[e] || !chainEligible(g.edgeKind[e])) continue;
      // v11: a chain draws at ONE width, so a width change (class boundary —
      // trunk→lane, street→era-widened street) terminates it; the joint sits at
      // a junction where the fillet disc covers the butt.
      if (Math.abs(edgeWidthOf(g, e) - arrW) > 0.3) continue;
      const outH = edgeHeading(g, e, node); // heading LEAVING the node
      if (outH === null) continue;
      const dot = arrH[0] * outH[0] + arrH[1] * outH[1]; // 1 = straight through
      if (dot > bestDot + 1e-9) { bestDot = dot; best = e; }
    }
    return best;
  };

  /**
   * Would edge `e0`, ARRIVING at `node` (i.e. `node` is the far end of `e0`), have an
   * aligned unvisited continuation LEAVING `node`? If so, `node` is INTERIOR to `e0`'s
   * chain and must NOT seed a walk (seeding there splits the road mid-chain — the
   * fragmentation finding). Ignores the `visited` guard so it reflects the graph's
   * geometry, not the walk order. `node` here is the edge's endpoint that would be the
   * seed origin, so the "arrival" heading is the edge LEAVING that origin — i.e. the
   * heading INTO the node is edgeHeading(e0, node); a continuation would then leave the
   * OTHER endpoint. See hasAlignedContinuationAt below for the symmetric per-end test.
   */
  const hasAlignedContinuation = (node: number, e0: number): boolean => {
    // Heading of e0 arriving INTO `node` (from node toward the other endpoint reversed).
    const away = edgeHeading(g, e0, node); // LEAVING node along e0
    if (away === null) return false;
    const arrH: [number, number] = [-away[0], -away[1]]; // heading INTO node along e0
    const arrW = edgeWidthOf(g, e0);
    for (const e of inc[node]) {
      if (e === e0 || degenerate[e] || !chainEligible(g.edgeKind[e])) continue;
      if (Math.abs(edgeWidthOf(g, e) - arrW) > 0.3) continue; // v11: mirrors the walk
      const outH = edgeHeading(g, e, node);
      if (outH === null) continue;
      const dot = arrH[0] * outH[0] + arrH[1] * outH[1];
      if (dot > Math.cos(CONTINUE_MAX_TURN) - 1e-9) return true;
    }
    return false;
  };

  const chains: RoadChain[] = [];
  const walkFrom = (t: number, e0: number): void => {
    const kind = g.edgeKind[e0];
    const width = edgeWidthOf(g, e0);
    const nodes: number[] = [t];
    let cur = t, e = e0;
    for (let guard = 0; guard <= nEdges; guard++) {
      visited[e] = 1;
      const nxt = other(e, cur);
      nodes.push(nxt);
      const ne = bestContinuation(nxt, e);
      if (ne < 0 || visited[ne] || nxt === t) break; // terminate (also break simple loops back to start)
      e = ne; cur = nxt;
    }
    chains.push({ kind, width, nodes, firstEdge: e0, lastEdge: e });
  };

  // 1) Seed ONLY from TRUE chain ENDS: a node/edge pair where the edge has no aligned
  //    unvisited continuation at that node (so `t` genuinely terminates `e0`'s chain).
  //    The old pass seeded from EVERY node ascending, so if an interior node had a
  //    lower index than both terminals it started the walk MID-chain and split the
  //    road at a gentle bend (the fragmentation finding — 10 joints). Seeding only at
  //    true ends means the walk always spans a maximal chain end-to-end; nodes ascend
  //    so the lowest-index end of each chain wins deterministically.
  for (let t = 0; t < nNodes; t++) {
    for (const e0 of inc[t]) {
      if (visited[e0] || degenerate[e0] || !chainEligible(g.edgeKind[e0])) continue;
      if (hasAlignedContinuation(t, e0)) continue; // interior — not a chain end
      walkFrom(t, e0);
    }
  }
  // 2) Anything left is a PURE cycle (every node had an aligned continuation, so no
  //    true-end seed reached it) — start at the lowest unvisited edge's endpoint A.
  for (let e0 = 0; e0 < nEdges; e0++) {
    if (visited[e0] || degenerate[e0] || !chainEligible(g.edgeKind[e0])) continue;
    walkFrom(g.edgeA[e0], e0);
  }
  return { chains };
}

/**
 * A neighbour region made available to cross-border stitching (D4(c)): its graph
 * and its chart frame, so a home chain ending at a border port can continue into it.
 * Only RESIDENT neighbours are passed (the caller gates on index residency); a chain
 * whose port has no resident neighbour simply ends — no artifact.
 */
export interface NeighborRegion { g: CityGraph; frame: RegionFrame }

/**
 * Build cross-border-stitched, home-chart control-point chains for one region.
 *
 * Runs {@link smoothChains} on the home graph, then for any chain that TERMINATES at
 * a home NODE_PORT tries to extend it into the matching port of a resident neighbour
 * (a port within BORDER_PORT_MATCH_M in 3D). The neighbour's continuing chain is
 * transformed into the HOME chart (localToDir on the neighbour frame → dirToLocal on
 * the home frame) and appended, so a road flowing cell-to-cell smooths as ONE curve
 * with no border kink. Each neighbour edge is consumed at most once (a per-neighbour
 * visited set), so the stitch never duplicates a strip.
 *
 * Returns resolved (u,v) control points in the HOME chart — the input to
 * {@link chainCentreline}. With no neighbours this is exactly smoothChains mapped to
 * node coordinates, so the non-resident-neighbour case is graceful by construction.
 */
export function stitchedChains(
  g: CityGraph,
  neighbors: NeighborRegion[] = [],
): { chains: RoadChainCP[] } {
  const home = g.frame;
  const { chains } = smoothChains(g);

  // Pre-chain every neighbour once so a home port can graft the neighbour's run.
  const nb = neighbors.map((n) => ({ ...n, sc: smoothChains(n.g).chains, used: new Set<number>() }));

  // Home port lookup: 3D dir of each NODE_PORT, for matching a neighbour port.
  const d: number[] = [0, 0, 0];
  const homePortDir = (node: number): [number, number, number] => {
    localToDir(home, g.nodeU[node], g.nodeV[node], d);
    return [d[0], d[1], d[2]];
  };
  const isPort = (gg: CityGraph, node: number): boolean => gg.nodeKind[node] === NODE_PORT;

  // Convert a neighbour node (u,v) into HOME-chart (u,v) via the shared 3D dir.
  const toHome = (frame: RegionFrame, u: number, v: number): [number, number] => {
    localToDir(frame, u, v, d);
    const out: [number, number] = [0, 0];
    dirToLocal(home, d[0], d[1], d[2], out);
    return out;
  };

  const out: RoadChainCP[] = [];
  for (const ch of chains) {
    const u: number[] = [], v: number[] = [];
    for (const nd of ch.nodes) { u.push(g.nodeU[nd]); v.push(g.nodeV[nd]); }
    let endB = ch.nodes[ch.nodes.length - 1];

    // Try to extend at the tail end if it is a home port (the common seam case).
    const tail = ch.nodes[ch.nodes.length - 1];
    if (isPort(g, tail)) {
      const pd = homePortDir(tail);
      // Find the nearest matching neighbour port whose graph has a continuing chain.
      for (const n of nb) {
        let bestNode = -1, bestD = BORDER_PORT_MATCH_M;
        for (let i = 0; i < n.g.nodeU.length; i++) {
          if (!isPort(n.g, i)) continue;
          localToDir(n.frame, n.g.nodeU[i], n.g.nodeV[i], d);
          // 3D chord distance scaled to metres via the shared radius.
          const dd = Math.hypot(d[0] - pd[0], d[1] - pd[1], d[2] - pd[2]) * n.frame.radius;
          if (dd < bestD) { bestD = dd; bestNode = i; }
        }
        if (bestNode < 0) continue;
        // Graft the neighbour chain as TANGENT CONTEXT only — at most GRAFT_TANGENT_M
        // (a half-width) of it past the port, NOT the whole continuing road (which the
        // neighbour's own group already draws → 2 km of double-drawn ribbon, the
        // z-fight shimmer finding). The trimmed points seed the home chain's Catmull-Rom
        // tangent so the seam curves smoothly, but the ribbon rows stay on the home side.
        const halfW = ch.width * 0.5;
        const grafted = graftNeighborChain(n, bestNode, toHome, u, v, halfW);
        if (grafted) { endB = -1; break; } // continues into the neighbour — no flare
      }
    }
    out.push({
      kind: ch.kind, width: ch.width, u, v,
      endA: ch.nodes[0], endB, edgeA0: ch.firstEdge, edgeB0: ch.lastEdge,
    });
  }
  return { chains: out };
}

/**
 * How far (region-local metres, home chart) past the shared border port the neighbour
 * chain is grafted as Catmull-Rom TANGENT CONTEXT. The home ribbon rows only ever reach
 * this far onto the neighbour's side, so the neighbour's OWN group (which draws that
 * road in full) is never double-drawn — a seam-continuity tangent, not a re-draw. Kept
 * at a road half-width so the two cells' rows overlap by at most half a ribbon (well
 * under the fade band), enough to hide any residual sub-metre chart drift at the seam.
 */
const GRAFT_TANGENT_M = (dHalfW: number): number => Math.max(dHalfW, ROAD_SAMPLE_M);

/**
 * Append the neighbour chain incident to neighbour `portNode` onto the home control
 * points `(u,v)` — but only up to {@link GRAFT_TANGENT_M} PAST the port, as tangent
 * context, NOT the whole continuing road. The neighbour's own group draws its road in
 * full, so grafting the entire chain double-draws it (same centreline, different
 * tessellation, same polygonOffset → z-fight shimmer). We instead map the neighbour
 * nodes into the home chart and accumulate arc-length from the port; once the cap is
 * crossed we push ONE final control point interpolated exactly at the cap distance and
 * stop. That single point past the port defines the seam tangent so the home chain's
 * Catmull-Rom curves smoothly across the border, while the emitted ribbon rows stay on
 * the home side (at most a half-width overlap). Marks the neighbour chain used so it is
 * not stitched twice. Returns true iff a chain was grafted.
 */
function graftNeighborChain(
  n: { g: CityGraph; frame: RegionFrame; sc: RoadChain[]; used: Set<number> },
  portNode: number,
  toHome: (frame: RegionFrame, u: number, v: number) => [number, number],
  u: number[], v: number[],
  halfW: number,
): boolean {
  for (let ci = 0; ci < n.sc.length; ci++) {
    if (n.used.has(ci)) continue;
    const nodes = n.sc[ci].nodes;
    if (nodes.length < 2) continue;
    let seq: number[] | null = null;
    if (nodes[0] === portNode) seq = nodes;
    else if (nodes[nodes.length - 1] === portNode) seq = nodes.slice().reverse();
    if (seq === null) continue;
    n.used.add(ci);
    const cap = GRAFT_TANGENT_M(halfW);
    // The shared port in the home chart (already the home chain's tail control point).
    let [pu, pv] = toHome(n.frame, n.g.nodeU[seq[0]], n.g.nodeV[seq[0]]);
    let acc = 0; // arc-length past the port, home chart
    // Skip seq[0] (the shared port — the home chain already ends there).
    for (let i = 1; i < seq.length; i++) {
      const [hu, hv] = toHome(n.frame, n.g.nodeU[seq[i]], n.g.nodeV[seq[i]]);
      const seg = Math.hypot(hu - pu, hv - pv);
      if (acc + seg >= cap) {
        // Interpolate to exactly the cap and stop — one tangent-context point.
        const t = seg > 1e-9 ? (cap - acc) / seg : 0;
        u.push(pu + (hu - pu) * t); v.push(pv + (hv - pv) * t);
        return true;
      }
      u.push(hu); v.push(hv);
      acc += seg; pu = hu; pv = hv;
    }
    return true;
  }
  return false;
}

/**
 * Evaluate one centripetal (alpha = 0.5) Catmull-Rom segment p1→p2 at parameter
 * `tt` ∈ [0,1] (Barry–Goldman pyramid). `tt = 0` returns p1 exactly, `tt = 1`
 * returns p2 exactly, so the curve interpolates every control point (node-pass).
 * Degenerate knot spacing falls back to the straight p1→p2 lerp.
 */
function catmullRomPoint(
  x0: number, y0: number, x1: number, y1: number,
  x2: number, y2: number, x3: number, y3: number, tt: number,
): [number, number] {
  const t0 = 0;
  const t1 = t0 + Math.sqrt(Math.hypot(x1 - x0, y1 - y0)); // |Δ|^0.5 — centripetal knot
  const t2 = t1 + Math.sqrt(Math.hypot(x2 - x1, y2 - y1));
  const t3 = t2 + Math.sqrt(Math.hypot(x3 - x2, y3 - y2));
  if (!(t1 > t0) || !(t2 > t1) || !(t3 > t2)) return [x1 + (x2 - x1) * tt, y1 + (y2 - y1) * tt];
  const u = t1 + (t2 - t1) * tt;
  const a1x = ((t1 - u) * x0 + (u - t0) * x1) / (t1 - t0), a1y = ((t1 - u) * y0 + (u - t0) * y1) / (t1 - t0);
  const a2x = ((t2 - u) * x1 + (u - t1) * x2) / (t2 - t1), a2y = ((t2 - u) * y1 + (u - t1) * y2) / (t2 - t1);
  const a3x = ((t3 - u) * x2 + (u - t2) * x3) / (t3 - t2), a3y = ((t3 - u) * y2 + (u - t2) * y3) / (t3 - t2);
  const b1x = ((t2 - u) * a1x + (u - t0) * a2x) / (t2 - t0), b1y = ((t2 - u) * a1y + (u - t0) * a2y) / (t2 - t0);
  const b2x = ((t3 - u) * a2x + (u - t1) * a3x) / (t3 - t1), b2y = ((t3 - u) * a2y + (u - t1) * a3y) / (t3 - t1);
  const cx = ((t2 - u) * b1x + (u - t1) * b2x) / (t2 - t1), cy = ((t2 - u) * b1y + (u - t1) * b2y) / (t2 - t1);
  return [cx, cy];
}

/**
 * Re-tessellate one chain's node polyline (region-local u,v) into a smoothed
 * centreline via centripetal Catmull-Rom, at the shared ~ROAD_SAMPLE_M cadence,
 * with lateral deviation CLAMPED to ≤ `cap` metres from the straight chord of the
 * segment each sample belongs to (so the ribbon never leaves its carved bed).
 *
 * Endpoints of every segment (`s = 0`, `s = nSub`) are the node points exactly
 * (Catmull-Rom interpolates them), so the returned polyline PASSES THROUGH every
 * chain node and neighbouring rows are shared (each interior segment skips its
 * duplicate start row). Open chains get reflected phantom endpoints (a lone edge
 * with colinear reflections stays straight); a closed chain wraps its neighbours.
 */
export function chainCentreline(
  pu: number[], pv: number[], cap: number,
): { u: Float64Array; v: Float64Array } {
  const m = pu.length;
  if (m < 2) return { u: Float64Array.from(pu), v: Float64Array.from(pv) };
  const closed = m > 2 && pu[0] === pu[m - 1] && pv[0] === pv[m - 1];
  const n = closed ? m - 1 : m; // unique control-point count
  const px = (i: number): number => {
    if (closed) return pu[((i % n) + n) % n];
    if (i < 0) return 2 * pu[0] - pu[1];
    if (i > m - 1) return 2 * pu[m - 1] - pu[m - 2];
    return pu[i];
  };
  const py = (i: number): number => {
    if (closed) return pv[((i % n) + n) % n];
    if (i < 0) return 2 * pv[0] - pv[1];
    if (i > m - 1) return 2 * pv[m - 1] - pv[m - 2];
    return pv[i];
  };
  const segCount = closed ? n : m - 1;
  const u: number[] = [], v: number[] = [];
  for (let i = 0; i < segCount; i++) {
    const x1 = px(i), y1 = py(i), x2 = px(i + 1), y2 = py(i + 1);
    const chord = Math.hypot(x2 - x1, y2 - y1);
    const nSub = Math.max(1, Math.ceil(chord / ROAD_SAMPLE_M));
    const cl = chord || 1;
    const dux = (x2 - x1) / cl, dvy = (y2 - y1) / cl; // chord unit (lateral-clamp axis)
    const sStart = i === 0 ? 0 : 1; // interior segments skip the shared start row
    for (let s = sStart; s <= nSub; s++) {
      const tt = s / nSub;
      let [cu, cv] = catmullRomPoint(px(i - 1), py(i - 1), x1, y1, x2, y2, px(i + 2), py(i + 2), tt);
      // Clamp lateral offset from THIS chord to ≤ cap (endpoints have offset 0).
      const relx = cu - x1, rely = cv - y1;
      const along = relx * dux + rely * dvy;
      const perpx = relx - along * dux, perpy = rely - along * dvy;
      const perp = Math.hypot(perpx, perpy);
      if (perp > cap && perp > 1e-12) {
        const k = cap / perp;
        cu = x1 + along * dux + perpx * k;
        cv = y1 + along * dvy + perpy * k;
      }
      u.push(cu); v.push(cv);
    }
  }
  return { u: Float64Array.from(u), v: Float64Array.from(v) };
}

// ── PURE builder 1: road ribbons ──────────────────────────────────────────────

/**
 * Build terrain-following road ribbons for one region's CityGraph, in PLANET-LOCAL
 * 3D (ready to drop straight into `planet.group`).
 *
 * Per NON-TUNNEL edge:
 *   - TERRAIN edges (arterial/street/stair/terrace): a ribbon sampled every
 *     ~ROAD_SAMPLE_M along the chord — `nSeg = max(1, ceil(len / ROAD_SAMPLE_M))`
 *     segments — each ROW lifted to the sampler height at its CENTRELINE (u,v)
 *     plus ROAD_LIFT_M, so the ribbon hugs the ground station-by-station.
 *   - BRIDGE edges: subdivided the SAME way (`nSeg = max(1, ceil(len/ROAD_SAMPLE_M))`)
 *     but with no terrain follow — an ARCHED deck (endpoint chord + a parabolic lift
 *     that clears the crossing by BRIDGE_CLEARANCE_M, flush at both ends). Was a single
 *     flat plank; the arch makes an overpass read as an elevated crossing (D4(e)).
 *
 * FLAT CROSS-SECTION (both edges of a row share the centreline height): a road is
 * flat across its width, and the carve already flattens the full ROAD_W-wide bed
 * to a single `level`, so a centreline-height row matches the carved bed exactly —
 * sampling the two lateral edges independently instead would drape the ribbon onto
 * the carve SHOULDER, a metre-scale discontinuity right at the k=1 boundary.
 * TUNNEL edges are skipped entirely (they run beneath the surface).
 *
 * SMOOTHING (`smooth`, default OFF): when ON, ARTERIAL/STREET/TERRACE edges are
 * chained through degree-2 junctions and re-tessellated along a centripetal
 * Catmull-Rom curve ({@link smoothChains} + {@link chainCentreline}) — the fix for
 * "roads are essentially straight lines". STAIR and BRIDGE edges stay STRAIGHT in
 * either mode (stairs are filtered to ramps by the renderer; bridges are planks),
 * so their ends stay flush with ramps/cross-kind junctions. The DEFAULT is OFF so
 * collision/oracle consumers of the pure builder stay byte-stable; the renderer
 * passes ON. Row height is still the sampler height at the (curved) centreline, so
 * the smoothed ribbon hugs the ground the same way.
 *
 * COUNT RELATION (pinned by the oracle, smooth OFF): an eligible edge contributes
 * `(nSeg+1)*2` vertices, `nSeg` quads (`6*nSeg` indices), and `nSeg` per-quad
 * kind entries. Degenerate (zero-length) edges are skipped.
 *
 * GROUND-CONFORM (`index`, default OFF — user finding 2, ribbons that float): the
 * flat cross-section (both edges at the CENTRELINE height) is correct ONLY where the
 * carve flattened a full-width bed. On a CLIFF the carve is clamped/eased (k<1), so a
 * flat-across row leaves the outer edge floating above (or buried below) the real
 * slope. When an `index` is supplied, each ROW queries `index.carveAt` at its centre:
 * for `k ≥ RIBBON_CONFORM_K` the row stays flat-across (bit-for-bit the no-index
 * build — the carved bed IS flat there); for `k < RIBBON_CONFORM_K` each edge vertex
 * takes `k·(centreline height) + (1−k)·(ground at its OWN position)`, so the ribbon
 * drapes onto the slope at the edges and lifts to flat as the bed firms up. The
 * DEFAULT is OFF (index omitted) so every existing consumer stays byte-stable; the
 * renderer passes the live SettlementIndex. BRIDGE rows never conform (planks span).
 */
export function buildRoadStrips(
  g: CityGraph,
  sampler: PlanetSampler,
  smooth = false,
  index?: SettlementIndex,
  neighbors: NeighborRegion[] = [],
): { positions: Float32Array; indices: Uint32Array; kinds: Uint8Array } {
  const frame = g.frame;
  const R = sampler.def.radius;
  const positions: number[] = [];
  const indices: number[] = [];
  const kinds: number[] = [];
  const d: number[] = [0, 0, 0];
  // Scratch for the per-row carve query (only touched when an index is supplied).
  const wildScratch: TerrainSample = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };
  const carveOut: CarveHit = { level: 0, k: 0, kind: 0, dist: 0, half: 0, retaining: false };

  const sampleH = (u: number, v: number): number => {
    localToDir(frame, u, v, d);
    return sampler.height(d[0], d[1], d[2]); // metres (carved if the sampler carries the index)
  };
  const pushVertAt = (u: number, v: number, hMeters: number, lift = ROAD_LIFT_M): void => {
    localToDir(frame, u, v, d);
    const rad = R + hMeters + lift;
    positions.push(d[0] * rad, d[1] * rad, d[2] * rad);
  };

  /**
   * Per-row edge heights for a terrain-following (non-bridge) row. With no index
   * (or a fully-flattened bed, k ≥ RIBBON_CONFORM_K) BOTH edges take the centreline
   * height — byte-identical to the pre-conform flat-across build. On a clamped carve
   * each edge blends toward the ground at its own (u,v) by (1−k).
   */
  /**
   * Concave-DIP clearance (W2d-3 wrap, the "steps of base ground" finding): the
   * terrain mesh near settlements resolves ~16 m vertices, so between vertices it
   * draws straight CHORDS. Across a dip the chord spans ABOVE the analytic ground
   * the ribbon follows — burying the road in green stripes on every elevation
   * change. Measure local concavity as the positive second difference of the
   * analytic height at ±half a mesh cell along both chart axes, and ride the deck
   * that much higher (capped). Convex crests need nothing (chords sag BELOW).
   */
  const CLEAR_SAMPLE_M = 8;
  const CLEAR_CAP_M = 0.9;
  const dipClearance = (cu: number, cv: number, hC: number): number => {
    const a = (sampleH(cu - CLEAR_SAMPLE_M, cv) + sampleH(cu + CLEAR_SAMPLE_M, cv)) * 0.5 - hC;
    const b = (sampleH(cu, cv - CLEAR_SAMPLE_M) + sampleH(cu, cv + CLEAR_SAMPLE_M)) * 0.5 - hC;
    const c = a > b ? a : b;
    return c > 0 ? (c > CLEAR_CAP_M ? CLEAR_CAP_M : c) : 0;
  };
  /** Bank drape cap: an edge may ride at most this far ABOVE the row base to sit
   *  on a rising bank (the flare-mouth "ground cuts through the join" finding). */
  const DRAPE_CAP_M = 0.9;

  const rowHeights = (
    cu: number, cv: number,
    lu: number, lv: number, ru: number, rv: number,
  ): [number, number] => {
    const hC = sampleH(cu, cv); // leaves d = centreline unit dir…
    // …but the extra samples below REUSE that scratch, so capture it NOW (the
    // wild/carve probe must evaluate at the CENTRELINE, not the last offset point).
    const cdx = d[0], cdy = d[1], cdz = d[2];
    // Deck base = centreline height + dip clearance (above the coarse-mesh chord).
    const base = hC + dipClearance(cu, cv, hC);
    // Bank drape: an edge whose OWN ground rises above the deck rides up onto the
    // bank (capped) instead of tunnelling into it — the terrain mesh can no longer
    // cut a green wedge through a wide flare mouth on a cross-slope.
    const hOwnL = sampleH(lu, lv), hOwnR = sampleH(ru, rv);
    const drape = (h: number, own: number): number =>
      own > h + 0.02 ? (own - h > DRAPE_CAP_M ? h + DRAPE_CAP_M : own + 0.02) : h;
    if (index === undefined) return [drape(base, hOwnL), drape(base, hOwnR)];
    const wildH = sampler.sample(cdx, cdy, cdz, wildScratch, false, false, true).h;
    const has = index.carveAt(cdx, cdy, cdz, wildH, carveOut);
    const k = has ? carveOut.k : 0;
    if (k >= RIBBON_CONFORM_K) return [drape(base, hOwnL), drape(base, hOwnR)];
    // Clamped carve (cliff): lift each edge toward the flat centreline by k, but
    // never more than the cap off its own ground — a steep cliff cross-slope can't
    // float the edge. (Drape-up is implicit here: the blend follows own ground.)
    const cap = RIBBON_CONFORM_CAP_M;
    const liftL = k * (base - hOwnL), liftR = k * (base - hOwnR);
    const hL = hOwnL + (liftL < -cap ? -cap : liftL > cap ? cap : liftL);
    const hR = hOwnR + (liftR < -cap ? -cap : liftR > cap ? cap : liftR);
    return [hL, hR];
  };

  // Emit ONE straight edge as a per-edge ribbon (the original path). Used for ALL
  // non-tunnel edges when smooth is OFF, and for the non-chained STAIR/BRIDGE edges
  // when smooth is ON.
  const emitStraightEdge = (e: number): void => {
    const kind = g.edgeKind[e];
    const a = g.edgeA[e], b = g.edgeB[e];
    const au = g.nodeU[a], av = g.nodeV[a];
    const bu = g.nodeU[b], bv = g.nodeV[b];
    const du = bu - au, dv = bv - av;
    const len = Math.hypot(du, dv);
    if (len < 1e-6) return; // degenerate edge — nothing to draw
    const halfW = edgeWidthOf(g, e) * 0.5; // v11 per-edge width
    const pu = -dv / len, pv = du / len; // perpendicular unit in the (u,v) chart
    const bridge = kind === EDGE_BRIDGE;
    // Bridges are subdivided too (was 1 segment) so the deck can ARCH to clear its
    // crossing (D4(e)) instead of being a single flat plank at endpoint grade.
    const nSeg = Math.max(1, Math.ceil(len / ROAD_SAMPLE_M));
    const hA = bridge ? sampleH(au, av) : 0;
    const hB = bridge ? sampleH(bu, bv) : 0;
    // Deck arch: the max metres by which the terrain under the span (plus clearance)
    // rises above the straight endpoint chord — the deck lifts by this, tapered to 0
    // at the ends (a parabola), so the bridge clears what it crosses yet stays flush
    // with the roads at both ends. 0 on a normal valley/river span (chord clears the dip).
    let arch = 0;
    if (bridge) {
      for (let s = 1; s < nSeg; s++) {
        const t = s / nSeg;
        const gu = au + du * t, gv = av + dv * t;
        const need = (sampleH(gu, gv) + BRIDGE_CLEARANCE_M) - (hA + (hB - hA) * t);
        const taper = 4 * t * (1 - t); // 0 at ends, 1 at mid
        if (taper > 1e-6) { const lift = need / taper; if (lift > arch) arch = lift; }
      }
    }
    const base = positions.length / 3;
    for (let s = 0; s <= nSeg; s++) {
      const t = s / nSeg;
      const cu = au + du * t, cv = av + dv * t;
      const luu = cu + pu * halfW, lvv = cv + pv * halfW;
      const ruu = cu - pu * halfW, rvv = cv - pv * halfW;
      if (bridge) {
        // Arched deck: endpoint chord + parabolic arch (flush ends, clears the crossing).
        const h = hA + (hB - hA) * t + arch * 4 * t * (1 - t);
        pushVertAt(luu, lvv, h);
        pushVertAt(ruu, rvv, h);
      } else {
        const [hL, hR] = rowHeights(cu, cv, luu, lvv, ruu, rvv);
        pushVertAt(luu, lvv, hL); // left edge
        pushVertAt(ruu, rvv, hR); // right edge
      }
    }
    for (let s = 0; s < nSeg; s++) {
      const li = base + 2 * s, ri = li + 1, li1 = li + 2, ri1 = li + 3;
      indices.push(li, ri, li1, ri, ri1, li1);
      kinds.push(kind);
    }
  };

  // Per-node incident ROAD edges (chainEligible + bridges) — the flare pass asks
  // "what is the widest OTHER road at this chain's end node?" to size the mouth.
  const roadInc: number[][] = Array.from({ length: g.nodeU.length }, () => []);
  for (let e = 0; e < g.edgeKind.length; e++) {
    const k = g.edgeKind[e];
    if (!chainEligible(k) && k !== EDGE_BRIDGE) continue;
    const a = g.edgeA[e], b = g.edgeB[e];
    if (Math.hypot(g.nodeU[b] - g.nodeU[a], g.nodeV[b] - g.nodeV[a]) < 1e-6) continue;
    roadInc[a].push(e); roadInc[b].push(e);
  }
  /** Widest OTHER incident road half-width at `node` (0 = dead end → no flare). */
  const flareExtra = (node: number, ownEdge: number): number => {
    let best = 0;
    for (const e of roadInc[node]) {
      if (e === ownEdge) continue;
      const hw = edgeWidthOf(g, e) * 0.5;
      if (hw > best) best = hw;
    }
    return best;
  };

  // Emit ONE smoothed chain (resolved home-chart control points) as a continuous
  // Catmull-Rom ribbon. Cross-border-stitched chains carry neighbour control points
  // already mapped into the home chart, so a road flowing cell-to-cell reads as one.
  // W2d-3 wrap: a chain end that meets other roads opens a FLARE — the ribbon's own
  // half-width eases quadratically (a trumpet) up to own + FLARE_FRAC × the widest
  // other road's half-width over the flare zone, with rows densified to FLARE_ROW_M
  // so the mouth curve reads smooth. The whole chain rides a width-ordered lift
  // (wider on top, ordinal tie-break) so overlaps at junctions never z-fight.
  const emitSmoothChain = (ch: RoadChainCP, ordinal: number): void => {
    const m = ch.u.length;
    if (m < 2) return;
    const halfW = ch.width * 0.5;               // v11 per-edge (per-chain) width
    const cap = ch.width * LATERAL_CAP_FRAC;    // deviation cap follows the width
    const cpu = ch.u, cpv = ch.v;
    const cl = chainCentreline(cpu, cpv, cap);
    const nP = cl.u.length;
    if (nP < 2) return;
    // Cumulative arclength along the (dense ≤ ROAD_SAMPLE_M) centreline polyline.
    const s = new Float64Array(nP);
    for (let i = 1; i < nP; i++) s[i] = s[i - 1] + Math.hypot(cl.u[i] - cl.u[i - 1], cl.v[i] - cl.v[i - 1]);
    const total = s[nP - 1];
    if (total < 1e-6) return;
    // Mouth sizes at the two ends (0 = no flare: dead end or grafted border tail).
    // CORE COMPENSATION (user finding: "the blend overlaps the ground cutover"):
    // the fade profile eats ROAD_FADE_FRAC of the width EACH side, so an emitted
    // mouth only shows (1 − 2·FRAC) of it opaque — the mouths' and the crossing
    // road's transparent fades overlapped and grass showed through the join.
    // Scale the emitted extra so the OPAQUE core reaches the intended target.
    const coreK = 1 / (1 - 2 * ROAD_FADE_FRAC);
    const fA = ch.endA >= 0 ? flareExtra(ch.endA, ch.edgeA0) * FLARE_FRAC * coreK : 0;
    const fB = ch.endB >= 0 ? flareExtra(ch.endB, ch.edgeB0) * FLARE_FRAC * coreK : 0;
    const LA = fA > 0 ? Math.min(total * 0.45, Math.max(4, (halfW + fA) * FLARE_LEN_K)) : 0;
    const LB = fB > 0 ? Math.min(total * 0.45, Math.max(4, (halfW + fB) * FLARE_LEN_K)) : 0;
    // Row stations: every centreline point, plus subdivided rows inside flare zones.
    const stations: number[] = [];
    for (let i = 0; i < nP; i++) {
      stations.push(s[i]);
      if (i < nP - 1) {
        const seg = s[i + 1] - s[i];
        const touchesFlare = s[i] < LA || s[i + 1] > total - LB;
        if (touchesFlare && seg > FLARE_ROW_M) {
          const nSub = Math.ceil(seg / FLARE_ROW_M);
          for (let k = 1; k < nSub; k++) stations.push(s[i] + (seg * k) / nSub);
        }
      }
    }
    stations.sort((a, b) => a - b);
    // Dedup near-coincident stations (a subdivision landing on an existing row).
    const st: number[] = [stations[0]];
    for (let i = 1; i < stations.length; i++) if (stations[i] - st[st.length - 1] > 0.05) st.push(stations[i]);
    // Resolve stations to points on the centreline polyline (walk pointer).
    const rowU: number[] = [], rowV: number[] = [];
    let seg = 0;
    for (const sv of st) {
      while (seg < nP - 2 && s[seg + 1] < sv - 1e-9) seg++;
      const span = s[seg + 1] - s[seg] || 1;
      const tt = Math.min(1, Math.max(0, (sv - s[seg]) / span));
      rowU.push(cl.u[seg] + (cl.u[seg + 1] - cl.u[seg]) * tt);
      rowV.push(cl.v[seg] + (cl.v[seg + 1] - cl.v[seg]) * tt);
    }
    const nR = rowU.length;
    if (nR < 2) return;
    const lift = ROAD_LIFT_M + ch.width * LIFT_WIDTH_K + (ordinal % 5) * LIFT_JITTER_M;
    const base = positions.length / 3;
    for (let i = 0; i < nR; i++) {
      // Tangent by central difference on the row list → smooth normals through
      // segment joints AND through the densified flare rows.
      const i0 = i > 0 ? i - 1 : 0, i1 = i < nR - 1 ? i + 1 : nR - 1;
      let tu = rowU[i1] - rowU[i0], tv = rowV[i1] - rowV[i0];
      const tl = Math.hypot(tu, tv) || 1; tu /= tl; tv /= tl;
      const pu = -tv, pv = tu; // perpendicular (ribbon cross-axis)
      // Trumpet half-width: quadratic ease into each flared end.
      let hw = halfW;
      if (LA > 0 && st[i] < LA) { const x = 1 - st[i] / LA; hw += fA * x * x; }
      if (LB > 0 && st[i] > total - LB) { const x = 1 - (total - st[i]) / LB; hw += fB * x * x; }
      const cu = rowU[i], cv = rowV[i];
      const luu = cu + pu * hw, lvv = cv + pv * hw;
      const ruu = cu - pu * hw, rvv = cv - pv * hw;
      const [hL, hR] = rowHeights(cu, cv, luu, lvv, ruu, rvv); // conform on the curve
      pushVertAt(luu, lvv, hL, lift); // left edge
      pushVertAt(ruu, rvv, hR, lift); // right edge
    }
    for (let q = 0; q < nR - 1; q++) {
      const li = base + 2 * q, ri = li + 1, li1 = li + 2, ri1 = li + 3;
      indices.push(li, ri, li1, ri, ri1, li1);
      kinds.push(ch.kind);
    }
  };

  if (!smooth) {
    for (let e = 0; e < g.edgeKind.length; e++) {
      if (g.edgeKind[e] === EDGE_TUNNEL) continue;
      emitStraightEdge(e);
    }
  } else {
    // Smoothed ARTERIAL/STREET/TERRACE chains first — continued through junctions
    // (D4(b)) and stitched across resident cell borders (D4(c)) — then the non-
    // chained STAIR/BRIDGE edges straight (TUNNEL never drawn).
    const { chains } = stitchedChains(g, neighbors);
    for (let ci = 0; ci < chains.length; ci++) emitSmoothChain(chains[ci], ci);
    for (let e = 0; e < g.edgeKind.length; e++) {
      const kind = g.edgeKind[e];
      if (kind === EDGE_TUNNEL || kind === EDGE_ARTERIAL || kind === EDGE_STREET || kind === EDGE_TERRACE) continue;
      emitStraightEdge(e); // STAIR (renderer filters → ramp) + BRIDGE (plank)
    }
  }

  return {
    positions: new Float32Array(positions),
    indices: new Uint32Array(indices),
    kinds: new Uint8Array(kinds),
  };
}

// ── PURE builder 2: continuous retaining-wall geometry (from shared wallStrips) ─

/**
 * Extrude the shared `buildStructures().wallStrips` runs into ONE merged mesh of
 * continuous vertical quad-strip ribbons — REPLACING the old per-probe instanced
 * boxes (the "walls stick out / floating fragments" fix).
 *
 * PACKING IN (see `structures.ts`): runs laid end-to-end, each
 * `[N, x,y,z,cutH ×N]` — a leading point-count N (≥ 2) then 4 floats per point
 * (planet-local position on the CARVED level surface + cut depth in metres).
 *
 * GEOMETRY OUT: per run point exactly TWO vertices — a BOTTOM on the carved
 * surface `(x,y,z)` and a TOP lifted `cutH` along the OUTWARD RADIAL (so the wall
 * face rises to the retained grade) — and `(N-1)` quads bridging consecutive
 * points. Walls therefore come ONLY from runs: no isolated per-probe quads. The
 * ribbon is zero-thickness (rendered DoubleSide); its ends are the vertical point
 * edges, so a continuous run reads as one capped retaining wall.
 *
 * COUNT RELATION (pinned by the oracle): vertices = Σ N × 2; indices = Σ (N-1) × 6.
 */
export function buildWallGeometry(
  wallStrips: Float64Array,
): { positions: Float32Array; indices: Uint32Array } {
  const positions: number[] = [];
  const indices: number[] = [];
  let i = 0;
  while (i < wallStrips.length) {
    const N = wallStrips[i]; i++;
    const base = positions.length / 3; // first vertex index of this run
    for (let p = 0; p < N; p++) {
      const o = i + p * 4;
      const x = wallStrips[o], y = wallStrips[o + 1], z = wallStrips[o + 2], cut = wallStrips[o + 3];
      const rad = Math.hypot(x, y, z) || 1;
      const ux = x / rad, uy = y / rad, uz = z / rad; // outward radial at this point
      positions.push(x, y, z);                                  // bottom (on the carve)
      positions.push(x + ux * cut, y + uy * cut, z + uz * cut); // top (retained grade)
    }
    // Bridge consecutive point-pairs: bottom/top of p → bottom/top of p+1.
    for (let p = 0; p < N - 1; p++) {
      const b0 = base + 2 * p, t0 = b0 + 1, b1 = b0 + 2, t1 = b0 + 3;
      indices.push(b0, b1, t1, b0, t1, t0);
    }
    i += N * 4;
  }
  return { positions: new Float32Array(positions), indices: new Uint32Array(indices) };
}

// ── PURE builder 3: one stepped stair ramp for a single STAIR edge ─────────────

/**
 * Build the treads of ONE stepped stair ramp between two carved edge ends, in
 * PLANET-LOCAL 3D. Replaces the old scattered per-edge step boxes (the "stairs are
 * random / don't connect" fix): treads march the STRAIGHT 3D chord between the two
 * end positions, so the ramp connects FLUSH to the road ribbons that terminate at
 * the same nodes.
 *
 * Inputs: `(ax,ay,az)`/`(bx,by,bz)` UNIT directions at ends A/B; `hA`/`hB` the
 * end GROUND RADII (`R + carved height`, with whatever lift the caller's ribbons
 * use — the renderer passes the ROAD_LIFT_M-matched radius so ends coincide with
 * the adjacent ribbon end centres).
 *
 * Output: `treads` = `[cx,cy,cz, yaw, w, run, rise]` per tread
 *   - (cx,cy,cz): tread CENTRE on the 3D chord at t=(i+0.5)/n.
 *   - yaw: chord heading in the {@link tangentFrame} at the centre (informational
 *     / width-axis contract; the renderer orients treads along the full 3D chord).
 *   - w = ROAD_W.stair; run = |chord| / n (along-travel span); rise = STAIR_RISE_M.
 *
 * TREAD COUNT (pinned by the oracle): `n = max(1, ceil(|hB−hA| / STAIR_RISE_M))`.
 * FLUSH ENDS: `centre₀ − travel·run/2 = A` and `centre_{n−1} + travel·run/2 = B`
 * exactly (travel = normalize(B−A)) — first/last tread meet the given ends.
 */
export function buildStairRamp(
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  hA: number, hB: number,
): { treads: Float32Array } {
  const pAx = ax * hA, pAy = ay * hA, pAz = az * hA;
  const pBx = bx * hB, pBy = by * hB, pBz = bz * hB;
  const nTreads = Math.max(1, Math.ceil(Math.abs(hB - hA) / STAIR_RISE_M));
  const w = ROAD_W.stair;
  const ex = pBx - pAx, ey = pBy - pAy, ez = pBz - pAz; // full 3D chord
  const chordLen = Math.hypot(ex, ey, ez) || 1;
  const run = chordLen / nTreads;
  const treads: number[] = [];
  for (let i = 0; i < nTreads; i++) {
    const t = (i + 0.5) / nTreads;
    const cx = pAx + ex * t, cy = pAy + ey * t, cz = pAz + ez * t;
    const f = tangentFrame(cx, cy, cz);
    const yaw = Math.atan2(
      ex * f.t2[0] + ey * f.t2[1] + ez * f.t2[2],
      ex * f.t1[0] + ey * f.t1[1] + ez * f.t1[2],
    );
    treads.push(cx, cy, cz, yaw, w, run, STAIR_RISE_M);
  }
  return { treads: new Float32Array(treads) };
}

/**
 * Build the two SIDE APRONS (vertical skirts) of one stepped stair ramp — the fix
 * for "stairs have floating sides / see-through gaps" (finding 6b). Each side is a
 * quad-strip skirt running the length of the ramp: at every tread boundary (nTreads+1
 * columns) a TOP vertex sits at the tread-top on that side and a BOTTOM vertex sits
 * `STAIR_APRON_DROP_M` BELOW the sampled ground directly beneath it, so the skirt
 * always meets — or dips under — the terrain and never floats or leaves a gap.
 *
 * PURE (no THREE, no sampler object): the ground comes from the `groundRad` callback
 * (unit dir → planet-local surface radius = R + carved height), so it is node-testable
 * with a synthetic ground. Inputs mirror {@link buildStairRamp}: `(ax..az)`/`(bx..bz)`
 * UNIT dirs and `hA`/`hB` end radii (`R + carved height + lift`, as the renderer passes).
 *
 * COUNT RELATION (pinned by the oracle): with `nTreads = max(1, ceil(|hB−hA| /
 * STAIR_RISE_M))`, positions = `4·(nTreads+1)` vertices, indices = `12·nTreads`.
 */
export function buildStairApron(
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  hA: number, hB: number,
  groundRad: (dx: number, dy: number, dz: number) => number,
): { positions: Float32Array; indices: Uint32Array } {
  const pAx = ax * hA, pAy = ay * hA, pAz = az * hA;
  const pBx = bx * hB, pBy = by * hB, pBz = bz * hB;
  const nTreads = Math.max(1, Math.ceil(Math.abs(hB - hA) / STAIR_RISE_M));
  const halfW = ROAD_W.stair * 0.5;
  const ex = pBx - pAx, ey = pBy - pAy, ez = pBz - pAz; // full 3D chord
  const chordLen = Math.hypot(ex, ey, ez) || 1;
  const tvx = ex / chordLen, tvy = ey / chordLen, tvz = ez / chordLen; // travel unit
  const positions: number[] = [];
  const indices: number[] = [];
  for (let side = -1; side <= 1; side += 2) {
    const base = positions.length / 3;
    for (let i = 0; i <= nTreads; i++) {
      const t = i / nTreads;
      const cx = pAx + ex * t, cy = pAy + ey * t, cz = pAz + ez * t; // chord point
      const rc = Math.hypot(cx, cy, cz) || 1;
      const ux = cx / rc, uy = cy / rc, uz = cz / rc; // outward radial
      // lateral (width) axis = travel × radial
      let lx = tvy * uz - tvz * uy, ly = tvz * ux - tvx * uz, lz = tvx * uy - tvy * ux;
      const ll = Math.hypot(lx, ly, lz) || 1; lx /= ll; ly /= ll; lz /= ll;
      // outer edge point at ±halfW, re-projected to a unit direction
      const oxp = cx + lx * side * halfW, oyp = cy + ly * side * halfW, ozp = cz + lz * side * halfW;
      const ol = Math.hypot(oxp, oyp, ozp) || 1;
      const edx = oxp / ol, edy = oyp / ol, edz = ozp / ol; // outer-edge unit dir
      const topRad = rc + STAIR_RISE_M; // tread-top level on this side
      const botRad = groundRad(edx, edy, edz) - STAIR_APRON_DROP_M; // below the ground
      positions.push(edx * topRad, edy * topRad, edz * topRad); // top
      positions.push(edx * botRad, edy * botRad, edz * botRad); // bottom
    }
    for (let i = 0; i < nTreads; i++) {
      const tp0 = base + 2 * i, bt0 = tp0 + 1, tp1 = tp0 + 2, bt1 = tp0 + 3;
      indices.push(tp0, tp1, bt1, tp0, bt1, bt0);
    }
  }
  return { positions: new Float32Array(positions), indices: new Uint32Array(indices) };
}

// ── PURE builder 4: the building KIT layout (walls/roofs/doors/windows/chimneys) ─
//
// The user's "houses are just boxes" finding: each shared `buildStructures`
// building box is expanded into a small KIT of oriented-box (and pitched-cone)
// PARTS — a wall body, a pitched-or-flat roof, a road-facing door, 2–4 windows,
// and a chimney on ~30%. The roll lives SHARED-side in `buildStructures` (fields
// 9–12 of the buildings stride, drawn from the salt-0xb1d6 per-parcel sub-stream
// at drawIndex ≥ 1 so building heights never shift — see structures.ts), so this
// function is a PURE geometry expander over the buildings array alone: same input
// → same parts, testable in node with no THREE. The renderer instances the parts
// by kind; the oracle checks their counts, door bearing, and radial/footprint
// containment.
//
// Every part is an oriented box given as (centre `c`, orthonormal axes x/y/z,
// full extents `s`): the renderer composes a Matrix4 via makeBasis(x,y,z)+scale,
// and a pitched roof swaps the unit box for a 4-sided cone of the same bounds.
// `y` is the outward-radial ("up") axis for every part, so radial spread is the
// up-extent — the containment oracle relies on this.

/** Door quad: full width / height fraction of the wall / thickness (metres). */
const DOOR_W_M = 1.1;
const DOOR_H_FRAC = 0.55;
const DOOR_T_M = 0.16;
/** Window quad full width / height / thickness (metres). */
const WINDOW_W_M = 0.9;
const WINDOW_H_M = 1.0;
const WINDOW_T_M = 0.12;
/** Chimney cross-section (metres); it rises through the roof by roofH + this. */
const CHIMNEY_W_M = 0.6;
const CHIMNEY_OVER_M = 0.4;

export type KitPartKind = "wall" | "roof" | "door" | "window" | "chimney";

/** One oriented-box part of a building kit (a pitched roof renders as a cone). */
export interface KitPart {
  kind: KitPartKind;
  b: number; // owning building index
  pitched: boolean; // roof cone vs slab; false for every non-roof part
  c: [number, number, number]; // world centre (planet-local)
  x: [number, number, number]; // local X unit axis (→ extent s[0])
  y: [number, number, number]; // local Y unit axis — outward radial (→ s[1])
  z: [number, number, number]; // local Z unit axis (→ s[2])
  s: [number, number, number]; // FULL box extents
}

/** Per-building kit summary (bounds the oracle checks parts against). */
export interface BuildingKit {
  b: number;
  baseR: number; // grounded base radius
  h: number; // wall height
  roofH: number; // roof rise above the wall top
  cx: number; cy: number; cz: number; // grounded base centre
  ux: number; uy: number; uz: number; // outward radial (up) axis
}

/** Roof rise (metres) for a building of footprint side `minSide`. */
function roofHeightFor(pitched: boolean, minSide: number): number {
  return pitched ? Math.max(1.4, 0.4 * minSide) : 0.35;
}

/**
 * Expand `buildStructures().buildings` into a flat part list + per-building kit
 * summaries. PURE (no THREE) — the oracle in settlementmesh.test.mts pins its part
 * counts, door bearing, and containment; the renderer instances the parts by kind.
 */
export function buildingKitLayout(buildings: Float64Array): { parts: KitPart[]; kits: BuildingKit[] } {
  const parts: KitPart[] = [];
  const kits: BuildingKit[] = [];
  const nB = buildings.length / BUILDING_STRIDE;
  for (let b = 0; b < nB; b++) {
    const o = b * BUILDING_STRIDE;
    const bx = buildings[o], by = buildings[o + 1], bz = buildings[o + 2];
    const yaw = buildings[o + 3], w = buildings[o + 4], dd = buildings[o + 5];
    const h = buildings[o + 6], baseR = buildings[o + 8];
    const roofPitched = buildings[o + 9] > 0.5;
    const nWindows = Math.round(buildings[o + 10]);
    const hasChimney = buildings[o + 11] > 0.5;
    const doorYaw = buildings[o + 12];

    const f = tangentFrame(bx, by, bz);
    const U: [number, number, number] = [f.up[0], f.up[1], f.up[2]]; // outward radial
    const cyw = Math.cos(yaw), syw = Math.sin(yaw);
    // width axis = parcel u-heading in the tangent plane (⟂ up by construction).
    let wx = f.t1[0] * cyw + f.t2[0] * syw, wy = f.t1[1] * cyw + f.t2[1] * syw, wz = f.t1[2] * cyw + f.t2[2] * syw;
    const wl = Math.hypot(wx, wy, wz) || 1; wx /= wl; wy /= wl; wz /= wl;
    const W: [number, number, number] = [wx, wy, wz];
    // depth = width × up (RIGHT-HANDED body frame: x×y = z ⇒ det +1, so
    // Quaternion.setFromRotationMatrix gets a proper rotation — a left-handed
    // U×W here tilts/inverts the house, the V2 finding 4). ⟂ up by construction.
    let dx = wy * U[2] - wz * U[1], dy = wz * U[0] - wx * U[2], dz = wx * U[1] - wy * U[0];
    const dl = Math.hypot(dx, dy, dz) || 1; dx /= dl; dy /= dl; dz /= dl;
    const D: [number, number, number] = [dx, dy, dz];

    const minSide = Math.min(w, dd);
    const roofH = roofHeightFor(roofPitched, minSide);
    kits.push({ b, baseR, h, roofH, cx: bx, cy: by, cz: bz, ux: U[0], uy: U[1], uz: U[2] });

    // World centre from a (width, up, depth) offset off the grounded base centre.
    const at = (ow: number, ou: number, od: number): [number, number, number] => [
      bx + W[0] * ow + U[0] * ou + D[0] * od,
      by + W[1] * ow + U[1] * ou + D[1] * od,
      bz + W[2] * ow + U[2] * ou + D[2] * od,
    ];

    // WALL body box (spans [base, base+h] radially).
    parts.push({ kind: "wall", b, pitched: false, c: at(0, h * 0.5, 0), x: W, y: U, z: D, s: [w, h, dd] });

    // ROOF (spans [base+h, base+h+roofH]): pitched → cone, flat → thin slab.
    const roofC = at(0, h + roofH * 0.5, 0);
    if (roofPitched) parts.push({ kind: "roof", b, pitched: true, c: roofC, x: W, y: U, z: D, s: [w * 1.1, roofH, dd * 1.1] });
    else parts.push({ kind: "roof", b, pitched: false, c: roofC, x: W, y: U, z: D, s: [w * 1.02, roofH, dd * 1.02] });

    // The four lateral faces, in a fixed order (door + windows attach to these).
    const nW: [number, number, number] = [-W[0], -W[1], -W[2]];
    const nD: [number, number, number] = [-D[0], -D[1], -D[2]];
    const faces: { N: [number, number, number]; span: [number, number, number]; half: number; len: number }[] = [
      { N: D, span: W, half: dd * 0.5, len: w },
      { N: nD, span: W, half: dd * 0.5, len: w },
      { N: W, span: D, half: w * 0.5, len: dd },
      { N: nW, span: D, half: w * 0.5, len: dd },
    ];

    // Right-handed per-face basis third axis: z = x × y = span × up. Building this
    // by construction (rather than reusing fc.N, which for two of the four faces is
    // an IMPROPER triple with det −1) keeps every emitted part basis a proper
    // rotation. The box is symmetric in its z (thickness), so the outward offset —
    // computed from fc.N below — is unaffected by z's sign.
    const faceZ = (sp: [number, number, number]): [number, number, number] => {
      const zx = sp[1] * U[2] - sp[2] * U[1];
      const zy = sp[2] * U[0] - sp[0] * U[2];
      const zz = sp[0] * U[1] - sp[1] * U[0];
      return [zx, zy, zz];
    };

    // DOOR on the face whose outward normal is nearest the doorYaw bearing.
    const ddx = f.t1[0] * Math.cos(doorYaw) + f.t2[0] * Math.sin(doorYaw);
    const ddy = f.t1[1] * Math.cos(doorYaw) + f.t2[1] * Math.sin(doorYaw);
    const ddz = f.t1[2] * Math.cos(doorYaw) + f.t2[2] * Math.sin(doorYaw);
    let doorFace = 0, bestDot = -Infinity;
    for (let i = 0; i < 4; i++) {
      const N = faces[i].N;
      const dot = ddx * N[0] + ddy * N[1] + ddz * N[2];
      if (dot > bestDot) { bestDot = dot; doorFace = i; }
    }
    {
      const fc = faces[doorFace];
      const doorH = Math.min(h * DOOR_H_FRAC, h - 0.4);
      const doorW = Math.min(DOOR_W_M, fc.len * 0.6);
      const off = fc.half + DOOR_T_M * 0.5;
      const c: [number, number, number] = [
        bx + U[0] * (doorH * 0.5) + fc.N[0] * off,
        by + U[1] * (doorH * 0.5) + fc.N[1] * off,
        bz + U[2] * (doorH * 0.5) + fc.N[2] * off,
      ];
      parts.push({ kind: "door", b, pitched: false, c, x: fc.span, y: U, z: faceZ(fc.span), s: [doorW, doorH, DOOR_T_M] });
    }

    // WINDOWS at mid-height, one per face in fixed order (nWindows ≤ 4 faces).
    for (let i = 0; i < nWindows; i++) {
      const fc = faces[i % 4];
      const off = fc.half + WINDOW_T_M * 0.5;
      const winW = Math.min(WINDOW_W_M, fc.len * 0.5);
      const c: [number, number, number] = [
        bx + U[0] * (h * 0.5) + fc.N[0] * off,
        by + U[1] * (h * 0.5) + fc.N[1] * off,
        bz + U[2] * (h * 0.5) + fc.N[2] * off,
      ];
      parts.push({ kind: "window", b, pitched: false, c, x: fc.span, y: U, z: faceZ(fc.span), s: [winW, WINDOW_H_M, WINDOW_T_M] });
    }

    // CHIMNEY (~30%): rises from the wall top through the roof by roofH + over.
    if (hasChimney) {
      const chH = roofH + CHIMNEY_OVER_M;
      parts.push({
        kind: "chimney", b, pitched: false,
        c: at(w * 0.28, h + chH * 0.5, dd * 0.28), x: W, y: U, z: D, s: [CHIMNEY_W_M, chH, CHIMNEY_W_M],
      });
    }
  }
  return { parts, kits };
}

// ── shared unit geometry (module singletons — tiny, kept for the session) ──────

let unitBox: THREE.BoxGeometry | null = null;
function kitBox(): THREE.BoxGeometry {
  if (!unitBox) unitBox = new THREE.BoxGeometry(1, 1, 1); // centred (spans −0.5..0.5)
  return unitBox;
}

let unitRoofCone: THREE.ConeGeometry | null = null;
/** A 4-sided cone (hip roof) fitting a unit box footprint, CENTRED (spans −0.5..0.5). */
function kitRoofCone(): THREE.ConeGeometry {
  if (!unitRoofCone) {
    unitRoofCone = new THREE.ConeGeometry(Math.SQRT1_2, 1, 4);
    unitRoofCone.rotateY(Math.PI / 4);
  }
  return unitRoofCone;
}

// ── fountain water (2026-08-02 fountain overhaul) ─────────────────────────────
// ONE module clock shared BY REFERENCE into every water material's uniforms —
// materials are per-settlement (disposeVis frees them), the clock survives.
// update() ticks it from the frame time.
const WATER_TIME = { value: 0 };

/**
 * Kind-TINT for a road ribbon by EDGE_* kind — MULTIPLIED against the shared dirt/
 * gravel texture (so the texel detail shows through). Muted, near-neutral tints:
 * arterials read slightly DARKER (packed, worn), streets/terraces a touch lighter.
 * Night handling = Task 5.
 */
function roadColor(kind: number): THREE.Color {
  // ONE packed-earth tone for every GROUND road — trunk, lane, and alley match
  // (user finding, W2d-3 wrap: per-kind tints made a small path meet a visibly
  // different-coloured big road, and junction fillets — tinted by the WIDEST
  // incident kind — read as dark patches against the lighter path). The width
  // hierarchy carries the trunk-vs-lane read; the tint no longer has to.
  // Bridges keep their timber deck (a different material in-world, not a road tone).
  if (kind === EDGE_BRIDGE) return new THREE.Color(0.55, 0.44, 0.32); // bridge — timber
  return new THREE.Color(0.78, 0.73, 0.64); // packed earth — all ground roads
}

// ── PURE: faded ground-blending road material data (W2b-2.6 Task 4, finding 10) ─
//
// The stark, self-lit vertex-coloured ribbon is replaced by a LIGHTING-RESPECTING
// MeshLambertMaterial mapped with a procedural dirt/gravel texture, whose OUTER
// edges fade transparently into the terrain. Two pieces are canvas/WebGL-free so
// they can be oracle-tested in node; the renderer consumes them (canvas texture +
// shader-injected per-vertex alpha).

/** Procedural road texture edge (px) — a small tile, RepeatWrapping-ed by the UVs. */
export const ROAD_TEX_SIZE = 128;
/** FIXED seed for the texture PRNG — Math.random is forbidden (client/server look
 *  parity + build-to-build determinism); mulberry32 from @hyperlight/shared. */
export const ROAD_TEX_SEED = 0x9e3779b9;

/**
 * Generate the dirt/gravel road texture as a raw RGBA pixel array (size·size·4
 * bytes), PURE and canvas-independent — the renderer's CanvasTexture path just
 * blits this into an ImageData. Deterministic: a mulberry32 stream off the FIXED
 * {@link ROAD_TEX_SEED}, so two generations are byte-identical (the determinism
 * oracle). A muted dirt base with fine per-pixel grain plus sparse dark pebbles /
 * light grit, so the surface reads as a packed track rather than flat pavement.
 */
export function roadTexturePixels(size = ROAD_TEX_SIZE, seed = ROAD_TEX_SEED): Uint8ClampedArray {
  const rng = mulberry32(seed);
  const px = new Uint8ClampedArray(size * size * 4);
  const baseR = 150, baseG = 138, baseB = 120; // muted dirt
  for (let i = 0; i < size * size; i++) {
    const n = rng();       // fine tonal noise
    const grain = rng();   // grain-type roll
    let shade = (n - 0.5) * 40; // ±20 fine variation
    if (grain > 0.88) shade -= 30 + rng() * 40;      // sparse dark pebble
    else if (grain < 0.10) shade += 20 + rng() * 30; // sparse light grit
    const o = i * 4;
    px[o] = baseR + shade;
    px[o + 1] = baseG + shade;
    px[o + 2] = baseB + shade * 0.9; // slightly cooler blue channel
    px[o + 3] = 255;
  }
  return px;
}

/** Fraction of the FULL ribbon width, each side, whose alpha fades to 0 at the edge. */
export const ROAD_FADE_FRAC = 0.25;
/**
 * The 4 cross-section columns of a faded ribbon row, as lerp params L→R (0 = left
 * edge, 1 = right edge) and the matching per-vertex alpha. The central 1−2·FADE
 * band is opaque (α 1) and the outer FADE fraction each side fades to 0, so the
 * ribbon edge blends into the terrain instead of ending in a hard line.
 */
export const ROAD_COLUMN_T = [0, ROAD_FADE_FRAC, 0.5, 1 - ROAD_FADE_FRAC, 1] as const;
export const ROAD_COLUMN_ALPHA = [0, 1, 1, 1, 0] as const;
/** Gravel-texture repeat period, metres (RepeatWrapping tiles the small texture). */
const ROAD_TEX_TILE_M = 3;

/**
 * Widen the 2-column road strips ({@link buildRoadStrips}) into a faded 4-column
 * ribbon: each row's left/right edge vertices gain two inner (core) vertices, and a
 * per-vertex ALPHA (via {@link ROAD_COLUMN_ALPHA}) fades the outer {@link
 * ROAD_FADE_FRAC} of the width to 0 — an OPAQUE core with edges that dissolve into
 * the ground. PURE (no THREE): returns position/uv/alpha/index/kind arrays the
 * renderer drops into a BufferGeometry (the shader multiplies `diffuseColor.a` by
 * the alpha attribute — MeshLambert keeps its lighting). buildRoadStrips is left
 * UNTOUCHED (every existing 2-column oracle stays byte-stable); this is a render-
 * path widening only.
 *
 * COUNT RELATION (pinned by the oracle): per input quad → 8 vertices (2 rows × 4
 * columns), 3 sub-quads (18 indices), 3 per-sub-quad kind entries. Rows are emitted
 * per-quad (vertices duplicated at segment joins) so the widening needs no global
 * chain state; the shared noise texture hides the per-quad UV reset.
 */
export function fadeRoadRibbons(
  strips: { positions: Float32Array; indices: Uint32Array; kinds: Uint8Array },
  /**
   * Optional per-column ground clamp (W2d-3 wrap, the "sawtooth step mid-road"
   * finding): surface RADIUS at a planet-local position. When given, every OPAQUE
   * column vertex is pushed radially up to ≥ ground + 0.1 m — the deck surface
   * between a row's two source samples is a straight lerp, so a bank curving up
   * THROUGH the span serrated green triangles into the road. The added centre
   * column + this clamp pin the surface above ground at quarter-width resolution.
   */
  groundRadiusAt?: (x: number, y: number, z: number) => number,
): { positions: Float32Array; uvs: Float32Array; alpha: Float32Array; indices: Uint32Array; kinds: Uint8Array } {
  const positions: number[] = [];
  const uvs: number[] = [];
  const alpha: number[] = [];
  const indices: number[] = [];
  const kinds: number[] = [];
  const cols = ROAD_COLUMN_T.length; // 4
  const P = strips.positions;
  for (let q = 0; q < strips.kinds.length; q++) {
    const kind = strips.kinds[q];
    // Quad q corner vertices: row0 (L0,R0), row1 (L1,R1) — the index pattern the
    // road builders emit is [li, ri, li1, ri, ri1, li1] (li=L, ri=R per row).
    const L0 = strips.indices[q * 6 + 0], R0 = strips.indices[q * 6 + 1];
    const L1 = strips.indices[q * 6 + 2], R1 = strips.indices[q * 6 + 4];
    const rows: [number, number][] = [[L0, R0], [L1, R1]];
    // Per-quad along-length uv: row0 = 0, row1 = row centre-to-centre span.
    const c0x = (P[L0 * 3] + P[R0 * 3]) * 0.5, c0y = (P[L0 * 3 + 1] + P[R0 * 3 + 1]) * 0.5, c0z = (P[L0 * 3 + 2] + P[R0 * 3 + 2]) * 0.5;
    const c1x = (P[L1 * 3] + P[R1 * 3]) * 0.5, c1y = (P[L1 * 3 + 1] + P[R1 * 3 + 1]) * 0.5, c1z = (P[L1 * 3 + 2] + P[R1 * 3 + 2]) * 0.5;
    const rowLen = Math.hypot(c1x - c0x, c1y - c0y, c1z - c0z);
    const base = positions.length / 3;
    for (let r = 0; r < 2; r++) {
      const [Li, Ri] = rows[r];
      const lx = P[Li * 3], ly = P[Li * 3 + 1], lz = P[Li * 3 + 2];
      const rx = P[Ri * 3], ry = P[Ri * 3 + 1], rz = P[Ri * 3 + 2];
      const width = Math.hypot(lx - rx, ly - ry, lz - rz);
      const uy = (r === 0 ? 0 : rowLen) / ROAD_TEX_TILE_M;
      for (let c = 0; c < cols; c++) {
        const t = ROAD_COLUMN_T[c];
        let px = lx + (rx - lx) * t, py = ly + (ry - ly) * t, pz = lz + (rz - lz) * t;
        if (groundRadiusAt !== undefined && ROAD_COLUMN_ALPHA[c] === 1) {
          const pr = Math.hypot(px, py, pz) || 1;
          const gr = groundRadiusAt(px / pr, py / pr, pz / pr) + 0.1;
          if (pr < gr) { const k = gr / pr; px *= k; py *= k; pz *= k; }
        }
        positions.push(px, py, pz);
        uvs.push((t * width) / ROAD_TEX_TILE_M, uy);
        alpha.push(ROAD_COLUMN_ALPHA[c]);
      }
    }
    for (let c = 0; c < cols - 1; c++) {
      const a0 = base + c, b0 = base + c + 1, a1 = base + cols + c, b1 = base + cols + c + 1;
      indices.push(a0, b0, a1, b0, b1, a1); // same winding as the source quad
      kinds.push(kind);
    }
  }
  return {
    positions: new Float32Array(positions),
    uvs: new Float32Array(uvs),
    alpha: new Float32Array(alpha),
    indices: new Uint32Array(indices),
    kinds: new Uint8Array(kinds),
  };
}

// ── shared road texture + faded material (browser-only; canvas/WebGL) ──────────

let roadTex: THREE.CanvasTexture | null = null;
/**
 * The shared dirt/gravel road texture (module singleton — deterministic, tiny, kept
 * for the session like the unit kit geometry). Blits {@link roadTexturePixels} into
 * a 2D canvas and wraps it in a RepeatWrapping CanvasTexture.
 */
function roadTexture(): THREE.CanvasTexture {
  if (roadTex) return roadTex;
  const canvas = document.createElement("canvas");
  canvas.width = ROAD_TEX_SIZE;
  canvas.height = ROAD_TEX_SIZE;
  const ctx = canvas.getContext("2d")!;
  const img = ctx.createImageData(ROAD_TEX_SIZE, ROAD_TEX_SIZE);
  img.data.set(roadTexturePixels());
  ctx.putImageData(img, 0, 0);
  roadTex = new THREE.CanvasTexture(canvas);
  roadTex.wrapS = THREE.RepeatWrapping;
  roadTex.wrapT = THREE.RepeatWrapping;
  roadTex.colorSpace = THREE.SRGBColorSpace;
  return roadTex;
}

/**
 * The faded road material: a LIGHTING-RESPECTING MeshLambertMaterial mapped with the
 * gravel texture and tinted per-vertex by kind, whose per-vertex alpha attribute
 * (`aAlpha`) fades the ribbon edges. `onBeforeCompile` injection (sanctioned
 * approach (a)) multiplies `diffuseColor.a` by the attribute AFTER `<color_fragment>`
 * so Lambert's lighting is untouched. transparent + depthWrite so the ribbon blends
 * at its edges yet still occludes; polygonOffset keeps it off the carved ground
 * without z-fighting.
 */
function makeFadedRoadMaterial(): THREE.MeshLambertMaterial {
  // Roads carry the painted banding too (see world/stylizedShading.ts) — the
  // street surface is most of what you look at inside a town, so an unpainted
  // one draws a hard line right through the middle of the effect.
  const mat = new THREE.MeshLambertMaterial({
    map: roadTexture(),
    vertexColors: true,
    side: THREE.DoubleSide,
    transparent: true,
    depthWrite: true,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
  });
  mat.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", "#include <common>\nattribute float aAlpha;\nvarying float vAlpha;")
      .replace("#include <begin_vertex>", "#include <begin_vertex>\nvAlpha = aAlpha;");
    shader.fragmentShader = shader.fragmentShader
      .replace("#include <common>", "#include <common>\nvarying float vAlpha;")
      .replace("#include <color_fragment>", "#include <color_fragment>\ndiffuseColor.a *= vAlpha;");
  };
  mat.customProgramCacheKey = () => "faded-road-v1";
  addStylizedShading(mat, { weatherGround: true });
  return mat;
}

interface SettlementVis {
  planet: Planet;
  /** Planet index (into system.planets) — for re-deriving neighbours on a restitch. */
  pi: number;
  /** The built region cell — needed to re-gather neighbours when a restitch is queued. */
  cell: CellId;
  /** Settlement anchor, planet-local metres (region cell centre at surface). */
  center: THREE.Vector3;
  /**
   * Bitmask (bit i for the i-th of [−U,+U,−V,+V]) of which edge-neighbours were RESIDENT
   * when this group's roads were last stitched. When a neighbour becomes resident later
   * this mask no longer matches `svc.index.has` → the group is queued for restitch, so a
   * cell built before its neighbour keeps its seam updated (D4(c) late-residency fix).
   */
  neighborMask: number;
  group: THREE.Group | null;
  /** The wall bodies' shared material — its emissive is poked warm at night. */
  buildingMat: THREE.MeshLambertMaterial | null;
  /** The window quads' MeshBasic material — its colour is poked warm at night. */
  windowMat: THREE.MeshBasicMaterial | null;
  /** The lamp-head material (Task ST-2) — its emissive is poked warm at night. */
  lampMat: THREE.MeshLambertMaterial | null;
  /** Planet-local xyz per lamp base (3 floats each) — Task ST-4's nearest-N tracker. */
  lampPositions: Float32Array | null;
  /** Hollow-building doorways (2026-07-30 auto-doors), planet-local. */
  doors: DoorPlacement[];
  /** DEBUG (hl-house-labels): raw building records for the label overlay. */
  buildings: Float64Array | null;
  buildingRejections: StructureRejection[];
  sites: { settlement: RegionSettlement; roster: NpcDef[] }[];
}

/**
 * The settlement renderer: one per star system. Each frame
 * it materializes-or-fetches the resident region graphs near the camera and builds
 * their terrain-conforming geometry into `planet.group`, with a one-build-per-frame
 * BUILD_DIST/DISPOSE_DIST hysteresis so a warp that brings several settlements into
 * range trickles them in rather than spiking.
 */
export class SettlementRenderer {
  /** Built/near settlements, keyed `${planetIdx}:${packCell}`. */
  private vis = new Map<string, SettlementVis>();
  /** Region cells found to hold no renderable content — never re-fetched. */
  private empty = new Set<string>();
  /**
   * Built settlements flagged to RESTITCH (an edge-neighbour became resident since the
   * group was last stitched, so its seam is stale) — keyed like `vis`. Drained one-per-
   * frame through the SAME build budget as fresh builds, so a burst of neighbour arrivals
   * trickles rather than thundering a wave of rebuilds. A cell built before its neighbour
   * was resident kept an unstitched seam forever (the old double-draw papered over it);
   * now the neighbour's arrival (a neighbourMask change) re-queues the adjacent group.
   */
  private restitch = new Set<string>();
  private local = new THREE.Vector3();
  private sunLocal = new THREE.Vector3();
  private dir = new THREE.Vector3();
  /**
   * ST-4: the ENTIRE scene's real streetlamp lighting — a constant pool of
   * `STREETLIGHT_REAL_MAX` `THREE.PointLight`s (never per-region, never per-frame
   * allocated) that reposition onto the nearest lamp heads to the player. Created
   * once in the constructor; parked `visible = false` and re-parented cheaply
   * between planet groups as the player moves. NEVER disposed on a region teardown
   * — `disposeVis` only frees per-settlement meshes, not this scene-level pool.
   */
  private lampLights: THREE.PointLight[] = [];
  /** Frame counter — the nearest-N recompute is throttled to every 4th update(). */
  private lampTick = 0;
  private lampTmp = new THREE.Vector3();
  /** Freshly-built groups awaiting a shader pre-warm. Each is built INVISIBLE;
   *  the main loop compiles its shaders off-thread (renderer.compileAsync) and
   *  reveals it once ready, so a first-visit settlement never renders with an
   *  uncompiled shader (the compile/upload stall that spiked reflection + draw
   *  on first visit to an area). Drained via {@link takePendingWarm}. */
  private pendingWarm: THREE.Group[] = [];
  /** Last absolute frame time seen (fountain water clock delta source). */
  private lastWaterT: number | null = null;

  constructor(private system: StarSystem) {
    this.lampLights = Array.from({ length: STREETLIGHT_REAL_MAX }, () => {
      const l = new THREE.PointLight(0xffd9a0, 0, STREETLIGHT_LIGHT_RANGE_M, 1.6);
      // Kept visible=true for LIFE and parked on the always-present scene root, so
      // the scene's point-light count is a CONSTANT STREETLIGHT_REAL_MAX from the
      // first frame. three.js bakes NUM_POINT_LIGHTS into every lit material's
      // program; a visible-count flip (0→N) invalidates and SYNCHRONOUSLY
      // recompiles every lit material in the scene on the next render — the
      // one-frame stutter on first approach to a lit town. So "off" is INTENSITY 0,
      // never visible=false; the count never changes and nothing ever recompiles.
      l.intensity = 0;
      system.scene.add(l);
      return l;
    });
  }

  update(camPos: THREE.Vector3, tSec?: number): void {
    // Fountain water clock: accumulate a SMALL monotonic time from the frame
    // deltas (the absolute sim time is epoch-scaled — sin() of it is float
    // garbage in the shader).
    if (tSec !== undefined) {
      const dt = this.lastWaterT === null ? 0 : tSec - this.lastWaterT;
      this.lastWaterT = tSec;
      if (dt > 0 && dt < 2) WATER_TIME.value += dt;
    }
    let builtOne = false;
    // Dispose pass: any built settlement now beyond DISPOSE_DIST.
    for (const [key, v] of this.vis) {
      this.local.copy(camPos).sub(v.planet.pose.center).applyQuaternion(v.planet.pose.invQuat);
      if (this.local.distanceTo(v.center) > DISPOSE_DIST) {
        this.disposeVis(v);
        this.vis.delete(key);
      }
    }
    // Build pass: walk each planet's resident 3×3 region neighbourhood under the
    // camera; build the nearest not-yet-built settlement (one per frame).
    for (let pi = 0; pi < this.system.planets.length; pi++) {
      const planet = this.system.planets[pi];
      const svc = planet.regionService;
      if (svc === null) continue;
      this.local.copy(camPos).sub(planet.pose.center).applyQuaternion(planet.pose.invQuat);
      const R = planet.def.radius;
      const centerCell = dirToCell(R, LEVEL_REGION, this.local.x, this.local.y, this.local.z);
      for (const cell of this.neighbourhood(R, centerCell)) {
        const rk = packCell(cell);
        const key = `${pi}:${rk}`;
        if (this.vis.has(key) || this.empty.has(key)) continue;
        // D4(d) RESIDENCY ALIGNMENT — the BUILD RING == the RESIDENT 3×3 ring.
        // Old rule gated on a FIXED 6500 m BUILD_DIST that mismatched the topological
        // 3×3 residency ring: on a big planet a resident (hence visible) neighbour
        // cell's centre lies beyond 6500 m and its settlement was never built, so a
        // cross-border road ended at an invisible border. Now: build EVERY resident
        // cell of the camera's 3×3 (which is exactly the ring the LOD loop keeps
        // resident + the ring this walk visits), so the build ring ⊇ the visible ring
        // by construction. Residency lands a full split-ring BEFORE a settleLevel node
        // asks for its mesh (settlementTriggerWants fires at 2× threshold), so a
        // settlement is built AHEAD of the cell becoming close/visible — no hard pop.
        // D4(d) build gate — RESIDENCY ONLY (the deleted BUILD_DIST plays no part).
        if (!settlementBuildGate(rk, (k) => svc.index.has(k))) continue; // LOD loop will fold it
        const f = regionFrame(R, cell);
        const surf = planet.sampler.surfaceRadius(f.cx, f.cy, f.cz);
        const center = new THREE.Vector3(f.cx * surf, f.cy * surf, f.cz * surf);
        if (builtOne) continue; // budget: one build per frame
        const g = svc.region(cell);
        const hasRoads = this.hasRenderableEdge(g);
        const hasParcels = g.parcels.some((p) => p.poly.length >= 8);
        if (!hasRoads && !hasParcels) { this.empty.add(key); continue; }
        // D4(c) cross-border stitching: gather the RESIDENT neighbour graphs so a
        // chain ending at a border port continues into them (non-resident neighbours
        // are simply absent → the chain ends, no artifact).
        const neighbors = this.residentNeighbors(planet, svc, R, cell);
        const v: SettlementVis = {
          planet, pi, cell, center, neighborMask: this.neighborMask(svc, R, cell),
          group: null, buildingMat: null, windowMat: null,
          lampMat: null, lampPositions: null,
          doors: [],
          buildings: null,
          buildingRejections: [],
          sites: this.runtimeSites(planet, g),
        };
        this.buildVis(v, g, svc.index, neighbors);
        this.vis.set(key, v);
        builtOne = true;
      }
    }

    // Late-residency RESTITCH pass (D4(c) fix): a group built before a neighbour became
    // resident has a stale seam. Detect it by comparing each built group's neighbour
    // residency mask against the live index; a change queues exactly one restitch. Drain
    // through the SAME one-build-per-frame budget so a burst of arrivals trickles in.
    for (const [key, v] of this.vis) {
      const svc = v.planet.regionService;
      if (svc === null) continue;
      const mask = this.neighborMask(svc, v.planet.def.radius, v.cell);
      if (mask !== v.neighborMask) this.restitch.add(key);
    }
    if (!builtOne && this.restitch.size > 0) {
      // Restitch the lowest-keyed flagged group (deterministic order), one per frame.
      const key = [...this.restitch].sort()[0];
      this.restitch.delete(key);
      const v = this.vis.get(key);
      if (v !== undefined && v.planet.regionService !== null) {
        const svc = v.planet.regionService;
        const R = v.planet.def.radius;
        const g = svc.region(v.cell);
        const neighbors = this.residentNeighbors(v.planet, svc, R, v.cell);
        this.disposeVis(v); // tears down the stale group meshes/materials
        v.neighborMask = this.neighborMask(svc, R, v.cell);
        this.buildVis(v, g, svc.index, neighbors);
        builtOne = true;
      }
    }

    // Night-glow pass: poke every built settlement's building material emissive
    // warm when its patch of ground faces away from the sun (the established
    // material-poke idiom — one material-level glow, no per-window instancing).
    for (const v of this.vis.values()) {
      if (v.buildingMat === null) continue;
      const planet = v.planet;
      // Sun direction in planet-local space: the un-spun, negated planet centre.
      this.sunLocal.copy(planet.pose.center).multiplyScalar(-1).normalize().applyQuaternion(planet.pose.invQuat);
      this.dir.copy(v.center).normalize(); // settlement's outward radial (planet-local)
      const sunDot = this.sunLocal.dot(this.dir);
      const night = Math.min(1, Math.max(0, (0.06 - sunDot) / 0.18));
      // NO warm glow. The whole world (terrain, flora) just DIMS to a dark, cool-
      // tinted version of its daytime albedo at night; a warm emissive on the
      // buildings lit them warm while everything around them went dark and cool —
      // reading as "colours completely wrong until day". So the wall material only
      // gets a faint NEUTRAL floor (so buildings aren't pure black), letting the
      // dim cool ambient own the night hue exactly as it does for the terrain. The
      // window quads (v.windowMat) carry the only warm "inhabited" light.
      v.buildingMat.emissive.setRGB(night * 0.02, night * 0.021, night * 0.024);
      // The window quads carry the lit look: dead
      // glass by day, warm lamplight at night.
      if (v.windowMat !== null) {
        // 2026-08-02 (user: "windows glow incorrectly — mostly transparent"):
        // day panes are barely-there dark glass (the old 0.38-alpha grey read
        // as a flat glow on every facade); night keeps the warm lamplit look,
        // slightly softened.
        v.windowMat.color.setRGB(0.05 + 0.85 * night, 0.06 + 0.62 * night, 0.07 + 0.35 * night);
        v.windowMat.opacity = 0.14 + 0.56 * night;
      }
      // Streetlights (Task ST-3): lamp heads glow warm at night (the real
      // PointLight pool below casts the actual ground illumination).
      if (v.lampMat) v.lampMat.emissive.setRGB(night * 0.9, night * 0.62, night * 0.32);
    }

    // ST-4: drive the real PointLight pool onto the nearest lamps to the player.
    // Throttled to every 4th update() — between recomputes the lights stay put
    // (their planet.group carries the pose, so they track the world for free).
    this.lampTick++;
    if (this.lampTick % 4 === 0) this.repositionLampPool(camPos);
  }

  /**
   * Reposition the constant `lampLights` pool onto the ≤ STREETLIGHT_REAL_MAX lamp
   * HEADS nearest the player, across every resident settlement. Allocates NO lights
   * (the pool is fixed) — it only moves, re-parents, and toggles the existing six.
   *
   * Each resident settlement's `lampPositions` is planet-local, so the player is
   * transformed into that planet's frame (renderer.ts idiom) and `nearestLamps`
   * runs there; candidates are keyed to their planet so multi-planet residency is
   * handled. Each pooled light is parented to its lamp's `planet.group` (which
   * carries the planet pose) and placed at the lamp head in planet-local metres —
   * no world round-trip. Unused lights park `visible = false`, still parented.
   */
  private repositionLampPool(camPos: THREE.Vector3): void {
    const range2 = STREETLIGHT_LIGHT_RANGE_M * STREETLIGHT_LIGHT_RANGE_M;
    const candidates: { planet: Planet; x: number; y: number; z: number; d2: number; night: number }[] = [];
    for (const v of this.vis.values()) {
      if (v.lampPositions === null) continue;
      const planet = v.planet;
      // Player → this planet's local frame (same frame as v.lampPositions).
      this.lampTmp.copy(camPos).sub(planet.pose.center).applyQuaternion(planet.pose.invQuat);
      // Per-settlement night factor (reuses the night-pass math).
      this.sunLocal.copy(planet.pose.center).multiplyScalar(-1).normalize().applyQuaternion(planet.pose.invQuat);
      this.dir.copy(v.center).normalize();
      const sunDot = this.sunLocal.dot(this.dir);
      const night = Math.min(1, Math.max(0, (0.06 - sunDot) / 0.18));
      const near = nearestLamps(
        v.lampPositions, this.lampTmp.x, this.lampTmp.y, this.lampTmp.z, STREETLIGHT_REAL_MAX, range2,
      );
      for (const h of near) {
        const bx = v.lampPositions[h.idx * 3];
        const by = v.lampPositions[h.idx * 3 + 1];
        const bz = v.lampPositions[h.idx * 3 + 2];
        // Head point = base + outward-radial * post height (up = normalized base).
        const scale = STREETLIGHT_HEIGHT_M / (Math.hypot(bx, by, bz) || 1);
        candidates.push({
          planet,
          x: bx * (1 + scale),
          y: by * (1 + scale),
          z: bz * (1 + scale),
          d2: h.d2,
          night,
        });
      }
    }
    // Global nearest across all settlements: sort by squared distance, keep the
    // STREETLIGHT_REAL_MAX closest lamps.
    candidates.sort((a, b) => a.d2 - b.d2);
    // Distance ease-in: a lamp is at FULL intensity within `fadeStart`, then
    // smoothsteps down to 0 at the `range` edge — so a light fades UP as the player
    // approaches instead of popping on hard at the cutoff (the jarring "turn on").
    const range = STREETLIGHT_LIGHT_RANGE_M;
    const fadeStart = range * 0.5;
    const fadeSpan = range - fadeStart || 1;
    for (let i = 0; i < STREETLIGHT_REAL_MAX; i++) {
      const light = this.lampLights[i];
      const c = i < candidates.length ? candidates[i] : undefined;
      // "Off" == INTENSITY 0 parked on the always-visible scene root — NEVER
      // light.visible = false (that would change the scene's point-light count and
      // force a scene-wide shader recompile; see the constructor note).
      let target = 0;
      if (c !== undefined && c.night > 0.05) {
        const d = Math.sqrt(c.d2);
        let df = (range - d) / fadeSpan; // 1 within fadeStart, 0 at range
        df = df <= 0 ? 0 : df >= 1 ? 1 : df * df * (3 - 2 * df); // smoothstep
        target = c.night * 2.4 * df;
      }
      if (c === undefined || target <= 0.001) {
        if (light.parent !== this.system.scene) this.system.scene.add(light);
        light.intensity = 0;
        continue;
      }
      if (light.parent !== c.planet.group) c.planet.group.add(light); // cheap re-parent
      light.position.set(c.x, c.y, c.z);
      light.intensity = target;
    }
  }

  private hasRenderableEdge(g: CityGraph): boolean {
    for (let e = 0; e < g.edgeKind.length; e++) if (g.edgeKind[e] !== EDGE_TUNNEL) return true;
    return false;
  }

  /**
   * R1 D5 screenshot-harness readiness probe. Reports, for the given planet index +
   * region cell, whether the settlement group is (a) built, (b) known-empty (no
   * renderable content — a legitimately settled cell can still be empty), or (c)
   * still pending, plus how many restitch/build tasks remain queued. The capture
   * script polls this to settle on the target cell (build the whole resident ring,
   * drain restitches) before shooting, instead of guessing with a fixed sleep. No
   * side effects — pure reads of the vis/empty/restitch maps.
   */
  captureStatus(pi: number, rk: number): { built: boolean; empty: boolean; restitchPending: number } {
    const key = `${pi}:${rk}`;
    return {
      built: this.vis.has(key),
      empty: this.empty.has(key),
      restitchPending: this.restitch.size,
    };
  }

  private runtimeSites(planet: Planet, graph: CityGraph): { settlement: RegionSettlement; roster: NpcDef[] }[] {
    const chronicle = collapseRegion(planet.sampler, graph);
    const roster = indexRegionRoster(graph, chronicle, 128);
    return regionSettlements(planet.sampler, graph, chronicle).map((settlement) => {
      const indexed = roster.settlements.find((entry) => entry.settlementId === settlement.id);
      return { settlement, roster: indexed ? regionQuestRoster(graph, indexed) : [] };
    });
  }

  private siteWorld(
    planet: Planet, graph: CityGraph, u: number, v: number, out: THREE.Vector3,
  ): THREE.Vector3 {
    const dir: number[] = [0, 0, 0];
    localToDir(graph.frame, u, v, dir);
    const surface = planet.sampler.surfaceRadius(dir[0], dir[1], dir[2]);
    return out.set(dir[0], dir[1], dir[2]).multiplyScalar(surface)
      .applyQuaternion(planet.pose.quat).add(planet.pose.center);
  }

  /** Nearest grown settlement centre among currently resident/rendered regions. */
  nearest(pos: THREE.Vector3, slackM = 0): {
    settlement: RegionSettlement; roster: NpcDef[]; planet: Planet; dist: number; pos: THREE.Vector3;
  } | null {
    let best: { settlement: RegionSettlement; roster: NpcDef[]; planet: Planet; dist: number; pos: THREE.Vector3 } | null = null;
    let bestRadius = Infinity;
    const world = new THREE.Vector3();
    for (const v of this.vis.values()) {
      const graph = v.planet.regionService?.region(v.cell);
      if (!graph) continue;
      for (const site of v.sites) {
        this.siteWorld(v.planet, graph, site.settlement.u, site.settlement.v, world);
        const dist = world.distanceTo(pos);
        const radius = (TIER_PARAMS[site.settlement.tier]?.radiusM ?? 80) + slackM;
        if (dist > radius) continue;
        // CONTAINMENT beats centre proximity (2026-08-12, user report): a
        // metropolis' huge apron can swallow a village outright, and its centre
        // can even be the nearer one — the banner then announced the metro
        // while you stood in the village square. Among the perimeters you are
        // INSIDE, the most specific one (smallest radius) wins; distance only
        // breaks ties between equals.
        if (!best || radius < bestRadius || (radius === bestRadius && dist < best.dist)) {
          best = { ...site, planet: v.planet, dist, pos: world.clone() };
          bestRadius = radius;
        }
      }
    }
    return best;
  }

  /** Auto-doors (2026-07-30): doorways of `planet`'s built regions within
   *  `maxDist` of planet-local p, nearest not guaranteed — the caller ranks. */
  /** DEBUG: raw building records and rejected placements, including empty regions. */
  debugBuildingsFor(planet: Planet): { cell: CellId; buildings: Float64Array; rejections: StructureRejection[] }[] {
    const out: { cell: CellId; buildings: Float64Array; rejections: StructureRejection[] }[] = [];
    for (const v of this.vis.values()) {
      if (v.planet !== planet || v.group === null || v.buildings === null) continue;
      out.push({ cell: v.cell, buildings: v.buildings, rejections: v.buildingRejections });
    }
    return out;
  }

  doorsNear(planet: Planet, x: number, y: number, z: number, maxDist: number): { d: DoorPlacement; key: string; dist: number }[] {
    const out: { d: DoorPlacement; key: string; dist: number }[] = [];
    const m2 = maxDist * maxDist;
    for (const [key, v] of this.vis) {
      if (v.planet !== planet || v.group === null) continue;
      for (let i = 0; i < v.doors.length; i++) {
        const d = v.doors[i];
        const dx = d.x - x, dy = d.y - y, dz = d.z - z;
        const dist2 = dx * dx + dy * dy + dz * dz;
        if (dist2 <= m2) out.push({ d, key: `${key}:${i}`, dist: Math.sqrt(dist2) });
      }
    }
    return out;
  }

  /** Nearest physical quest board, used by interaction and HUD prompts. */
  nearestBoard(pos: THREE.Vector3, maxDist = 8): {
    settlement: RegionSettlement; roster: NpcDef[]; planet: Planet; dist: number; pos: THREE.Vector3;
  } | null {
    let best: { settlement: RegionSettlement; roster: NpcDef[]; planet: Planet; dist: number; pos: THREE.Vector3 } | null = null;
    const world = new THREE.Vector3();
    for (const v of this.vis.values()) {
      const graph = v.planet.regionService?.region(v.cell);
      if (!graph) continue;
      for (const site of v.sites) {
        const board = site.settlement.board;
        if (!board) continue;
        this.siteWorld(v.planet, graph, board.u, board.v, world);
        const dist = world.distanceTo(pos);
        if (dist <= maxDist && (!best || dist < best.dist)) best = {
          ...site, planet: v.planet, dist, pos: world.clone(),
        };
      }
    }
    return best;
  }

  /** Resolve a loaded settlement identity for quest markers. */
  byId(id: string): { settlement: RegionSettlement; roster: NpcDef[]; planet: Planet; pos: THREE.Vector3 } | null {
    const world = new THREE.Vector3();
    for (const v of this.vis.values()) {
      const site = v.sites.find((entry) => entry.settlement.id === id);
      if (!site) continue;
      const graph = v.planet.regionService?.region(v.cell);
      if (!graph) return null;
      return { ...site, planet: v.planet, pos: this.siteWorld(v.planet, graph, site.settlement.u, site.settlement.v, world).clone() };
    }
    return null;
  }

  loaded(): { settlement: RegionSettlement; roster: NpcDef[]; planet: Planet; pos: THREE.Vector3 }[] {
    const out: { settlement: RegionSettlement; roster: NpcDef[]; planet: Planet; pos: THREE.Vector3 }[] = [];
    const world = new THREE.Vector3();
    for (const v of this.vis.values()) {
      const graph = v.planet.regionService?.region(v.cell);
      if (!graph) continue;
      for (const site of v.sites) out.push({
        ...site, planet: v.planet,
        pos: this.siteWorld(v.planet, graph, site.settlement.u, site.settlement.v, world).clone(),
      });
    }
    return out;
  }

  /** The 3×3 region neighbourhood (cell + 4 edge + 4 corner), deduped. */
  private neighbourhood(R: number, cell: CellId): CellId[] {
    const negU = edgeNeighbor(R, cell, SIDE_NEG_U);
    const posU = edgeNeighbor(R, cell, SIDE_POS_U);
    const negV = edgeNeighbor(R, cell, SIDE_NEG_V);
    const posV = edgeNeighbor(R, cell, SIDE_POS_V);
    const all = [
      cell, negU, posU, negV, posV,
      edgeNeighbor(R, negU, SIDE_NEG_V), edgeNeighbor(R, negU, SIDE_POS_V),
      edgeNeighbor(R, posU, SIDE_NEG_V), edgeNeighbor(R, posU, SIDE_POS_V),
    ];
    const seen = new Set<number>();
    const out: CellId[] = [];
    for (const c of all) {
      const k = packCell(c);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(c);
    }
    return out;
  }

  /**
   * The RESIDENT edge-neighbour graphs of `cell` (D4(c) cross-border stitching):
   * the 4 edge neighbours whose region is already folded into the index. Only edge
   * neighbours share a border port with the home cell, so corners are skipped. A
   * neighbour that is NOT resident is omitted (its chain simply ends — no artifact),
   * so this never MATERIALIZES a fresh region (svc.index.has gates before svc.region,
   * which is a cache hit for resident cells). Keeps the per-build stitch bounded to
   * ≤4 already-resident graphs.
   */
  /**
   * Residency bitmask of `cell`'s 4 edge-neighbours (bit i for [−U,+U,−V,+V]) — the
   * snapshot a built group records so a later neighbour arrival (mask change) triggers a
   * restitch (D4(c) late-residency fix). Cheap: 4 `index.has` lookups, no materialize.
   */
  private neighborMask(svc: NonNullable<Planet["regionService"]>, R: number, cell: CellId): number {
    const sides = [SIDE_NEG_U, SIDE_POS_U, SIDE_NEG_V, SIDE_POS_V];
    let mask = 0;
    for (let i = 0; i < sides.length; i++) {
      if (svc.index.has(packCell(edgeNeighbor(R, cell, sides[i])))) mask |= 1 << i;
    }
    return mask;
  }

  private residentNeighbors(
    planet: Planet, svc: NonNullable<Planet["regionService"]>, R: number, cell: CellId,
  ): NeighborRegion[] {
    const out: NeighborRegion[] = [];
    for (const side of [SIDE_NEG_U, SIDE_POS_U, SIDE_NEG_V, SIDE_POS_V]) {
      const nc = edgeNeighbor(R, cell, side);
      if (!svc.index.has(packCell(nc))) continue; // non-resident → chain just ends
      out.push({ g: svc.region(nc), frame: regionFrame(R, nc) });
    }
    void planet;
    return out;
  }

  /** Build one settlement's terrain-conforming geometry into planet.group. */
  private buildVis(v: SettlementVis, g: CityGraph, index: SettlementIndex, neighbors: NeighborRegion[] = []): void {
    const { planet } = v;
    const group = new THREE.Group(); // positions are already planet-local (identity xform)

    // --- Roads: one merged, FADED ground-blending ribbon mesh (finding 10). The
    //     smoothed + ground-conforming 2-column strips are widened to a 4-column
    //     cross-section (fadeRoadRibbons) whose OUTER edges fade transparently into
    //     the terrain; a lighting-respecting MeshLambert material carries a gravel
    //     texture tinted per-vertex by kind. STAIR sub-quads are DROPPED here (they
    //     render as stepped ramps below). ---
    const strips = buildRoadStrips(g, planet.sampler, true, index, neighbors); // smoothed + stitched + conforming
    if (strips.indices.length > 0) {
      const faded = fadeRoadRibbons(strips, (dx, dy, dz) => planet.def.radius + planet.sampler.height(dx, dy, dz));
      const roadIdx: number[] = [];
      const colors = new Float32Array(faded.positions.length);
      const c = new THREE.Color();
      for (let q = 0; q < faded.kinds.length; q++) {
        const kind = faded.kinds[q];
        // W2d-3 wrap: GROUND roads are painted INTO the terrain (TerrainSample.road
        // SDF → gravel in the terrain shader) — walkable, step-free, exact joins by
        // construction. Only BRIDGES keep ribbon geometry (they span water); stairs
        // keep their stepped ramps below.
        if (kind !== EDGE_BRIDGE) continue;
        c.copy(roadColor(kind));
        for (let k = 0; k < 6; k++) {
          const vi = faded.indices[q * 6 + k];
          roadIdx.push(vi);
          colors[vi * 3] = c.r; colors[vi * 3 + 1] = c.g; colors[vi * 3 + 2] = c.b;
        }
      }
      if (roadIdx.length > 0) {
        const geo = new THREE.BufferGeometry();
        geo.setAttribute("position", new THREE.BufferAttribute(faded.positions, 3));
        geo.setAttribute("uv", new THREE.BufferAttribute(faded.uvs, 2));
        geo.setAttribute("aAlpha", new THREE.BufferAttribute(faded.alpha, 1));
        geo.setIndex(new THREE.BufferAttribute(new Uint32Array(roadIdx), 1));
        geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
        geo.computeVertexNormals();
        geo.computeBoundingSphere();
        const mat = makeFadedRoadMaterial();
        const mesh = new THREE.Mesh(geo, mat);
        mesh.userData.footstepSurface = 'wood'; // bridge decks (roads are terrain paint)
        mesh.receiveShadow = true;
        group.add(mesh);
      }
    }

    // --- Structures (shared): grounded building boxes + continuous wall runs. ---
    const struct = buildStructures(g, planet.sampler, index);
    v.buildings = struct.buildings;
    v.buildingRejections = struct.rejections;
    group.add(civicBuildingsView(g,planet.def.radius,regionCivicBuildings(g,planet.sampler)));
    const outdoors=buildRegionOutdoors(g,planet.sampler,struct.buildings);
    group.add(buildOutdoorsView(outdoors,p=>outdoorPlanetFrame(g,planet.def.radius,p)));
    const box = kitBox();
    const pos = new THREE.Vector3();
    const scale = new THREE.Vector3();
    const quat = new THREE.Quaternion();
    const xA = new THREE.Vector3();
    const yA = new THREE.Vector3();
    const zA = new THREE.Vector3();
    const basis = new THREE.Matrix4();

    // Walls: extrude the shared wallStrips runs into ONE merged mesh of continuous
    // vertical quad-strip ribbons (replaces the old per-segment instanced boxes).
    {
      const wg = buildWallGeometry(struct.wallStrips);
      if (wg.indices.length > 0) {
        const geo = new THREE.BufferGeometry();
        geo.setAttribute("position", new THREE.BufferAttribute(wg.positions, 3));
        geo.setIndex(new THREE.BufferAttribute(wg.indices, 1));
        geo.computeVertexNormals();
        geo.computeBoundingSphere();
        const wallMat = new THREE.MeshLambertMaterial({ color: 0x6a655c, side: THREE.DoubleSide });
        const mesh = new THREE.Mesh(geo, wallMat);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        group.add(mesh);
      }
    }

    // (R1 D6, 2026-07-10) Perimeter CITY walls are removed — no settlement gets a
    // defensive rampart (user finding). Only the retaining walls above are drawn.

    // Stairs: one stepped ramp per STAIR edge — treads march the 3D chord between
    // the carved end heights (ROAD_LIFT_M-matched, so the first/last tread meet the
    // adjacent road ribbons flush), flanked by two side stringers and SIDE APRONS —
    // vertical skirts down to the sampled ground so the ramp sides never float and
    // leave a see-through gap over a dropping slope (finding 6b).
    {
      const frame = g.frame;
      const R = planet.sampler.def.radius;
      const d: number[] = [0, 0, 0];
      const treadMats: THREE.Matrix4[] = [];
      const stringerMats: THREE.Matrix4[] = [];
      const apronPos: number[] = [];
      const apronIdx: number[] = [];
      const groundR = (dx: number, dy: number, dz: number): number => R + planet.sampler.height(dx, dy, dz);
      for (let e = 0; e < g.edgeKind.length; e++) {
        if (g.edgeKind[e] !== EDGE_STAIR) continue;
        const a = g.edgeA[e], b = g.edgeB[e];
        const au = g.nodeU[a], av = g.nodeV[a], bu = g.nodeU[b], bv = g.nodeV[b];
        if (Math.hypot(bu - au, bv - av) < 1e-6) continue;
        localToDir(frame, au, av, d);
        const adx = d[0], ady = d[1], adz = d[2];
        const hA = R + planet.sampler.height(adx, ady, adz) + ROAD_LIFT_M;
        localToDir(frame, bu, bv, d);
        const bdx = d[0], bdy = d[1], bdz = d[2];
        const hB = R + planet.sampler.height(bdx, bdy, bdz) + ROAD_LIFT_M;
        const { treads } = buildStairRamp(adx, ady, adz, bdx, bdy, bdz, hA, hB);
        // Full 3D chord — treads (and stringers) orient along it, so ends stay flush.
        const pAx = adx * hA, pAy = ady * hA, pAz = adz * hA;
        const pBx = bdx * hB, pBy = bdy * hB, pBz = bdz * hB;
        xA.set(pBx - pAx, pBy - pAy, pBz - pAz);
        const chordLen = xA.length() || 1;
        xA.multiplyScalar(1 / chordLen); // travel axis (tilted along the ramp)
        for (let i = 0; i < treads.length; i += 7) {
          const cx = treads[i], cy = treads[i + 1], cz = treads[i + 2];
          const w = treads[i + 4], run = treads[i + 5], rise = treads[i + 6];
          yA.set(cx, cy, cz).normalize(); // outward radial at the tread centre
          zA.crossVectors(xA, yA).normalize();
          yA.crossVectors(zA, xA).normalize();
          basis.makeBasis(xA, yA, zA);
          quat.setFromRotationMatrix(basis);
          pos.set(cx + yA.x * rise * 0.5, cy + yA.y * rise * 0.5, cz + yA.z * rise * 0.5);
          scale.set(run, rise, w);
          treadMats.push(new THREE.Matrix4().compose(pos, quat, scale));
        }
        // Side APRONS: a ground-following skirt per side, sealing the ramp sides
        // down to STAIR_APRON_DROP_M below the sampled ground (no floating sides).
        const apron = buildStairApron(adx, ady, adz, bdx, bdy, bdz, hA, hB, groundR);
        const vBase = apronPos.length / 3;
        for (let i = 0; i < apron.positions.length; i++) apronPos.push(apron.positions[i]);
        for (let i = 0; i < apron.indices.length; i++) apronIdx.push(apron.indices[i] + vBase);

        // Two side stringers spanning the whole ramp, offset ±w/2 laterally, each
        // EXTENDED downward so its bottom reaches the ground on that side (rather
        // than floating at chord level where the slope has dropped away).
        const w = ROAD_W.stair;
        const mx = (pAx + pBx) * 0.5, my = (pAy + pBy) * 0.5, mz = (pAz + pBz) * 0.5;
        const chordMidRad = Math.hypot(mx, my, mz) || 1;
        yA.set(mx, my, mz).normalize();
        zA.crossVectors(xA, yA).normalize(); // width axis
        yA.crossVectors(zA, xA).normalize();
        const railTop = STAIR_RISE_M * 1.6; // rail lip above the chord
        basis.makeBasis(xA, yA, zA);
        quat.setFromRotationMatrix(basis);
        for (let side = -1; side <= 1; side += 2) {
          // Lowest ground radius on this side (ramp ends + midpoint) → skirt bottom.
          let minGround = Infinity;
          for (const tt of [0, 0.5, 1]) {
            const cx = pAx + (pBx - pAx) * tt, cy = pAy + (pBy - pAy) * tt, cz = pAz + (pBz - pAz) * tt;
            const ox = cx + zA.x * side * w * 0.5, oy = cy + zA.y * side * w * 0.5, oz = cz + zA.z * side * w * 0.5;
            const ol = Math.hypot(ox, oy, oz) || 1;
            const gr = groundR(ox / ol, oy / ol, oz / ol);
            if (gr < minGround) minGround = gr;
          }
          const topRad = chordMidRad + railTop;
          const botRad = Math.min(minGround - STAIR_APRON_DROP_M, chordMidRad); // reach ground, never above the chord
          const railH = topRad - botRad;
          const off = (topRad + botRad) * 0.5 - chordMidRad; // box-centre radial offset from the chord mid
          pos.set(
            mx + zA.x * side * w * 0.5 + yA.x * off,
            my + zA.y * side * w * 0.5 + yA.y * off,
            mz + zA.z * side * w * 0.5 + yA.z * off,
          );
          scale.set(chordLen, railH, STAIR_STRINGER_W_M);
          stringerMats.push(new THREE.Matrix4().compose(pos, quat, scale));
        }
      }
      if (treadMats.length > 0) {
        // Same material FAMILY as the roads: the gravel texture, lighting-respecting
        // Lambert, muted tint (opaque — only the road ribbon edges fade).
        const stairMat = new THREE.MeshLambertMaterial({ color: 0x9a8f7d, map: roadTexture() });
        const inst = new THREE.InstancedMesh(box, stairMat, treadMats.length);
        inst.userData.footstepSurface = 'stone';
        for (let i = 0; i < treadMats.length; i++) inst.setMatrixAt(i, treadMats[i]);
        inst.instanceMatrix.needsUpdate = true;
        inst.computeBoundingSphere();
        inst.castShadow = true;
        inst.receiveShadow = true;
        group.add(inst);
      }
      if (stringerMats.length > 0) {
        const railMat = new THREE.MeshLambertMaterial({ color: 0x82786c, map: roadTexture() });
        const inst = new THREE.InstancedMesh(box, railMat, stringerMats.length);
        for (let i = 0; i < stringerMats.length; i++) inst.setMatrixAt(i, stringerMats[i]);
        inst.instanceMatrix.needsUpdate = true;
        inst.computeBoundingSphere();
        inst.castShadow = true;
        inst.receiveShadow = true;
        group.add(inst);
      }
      // Merged apron skirts (one double-sided mesh for every ramp's side skirts).
      if (apronIdx.length > 0) {
        const geo = new THREE.BufferGeometry();
        geo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(apronPos), 3));
        geo.setIndex(new THREE.BufferAttribute(new Uint32Array(apronIdx), 1));
        geo.computeVertexNormals();
        geo.computeBoundingSphere();
        const apronMat = new THREE.MeshLambertMaterial({ color: 0x82786c, map: roadTexture(), side: THREE.DoubleSide });
        const mesh = new THREE.Mesh(geo, apronMat);
        mesh.userData.footstepSurface = 'stone';
        mesh.receiveShadow = true;
        group.add(mesh);
      }
    }

    // --- Buildings: expand each grounded box into a KIT — wall body + pitched
    //     OR flat roof + a road-facing door + 2–4 windows + a chimney on ~30%
    //     (the "houses are just boxes" fix). Parts come from the PURE
    //     buildingKitLayout (shared 0xb1d6 rolls, drawIndex ≥ 1 — heights hold),
    //     instanced by kind: walls carry the age tint + night-emissive poke, and
    //     the window quads use a MeshBasic material poked warm at night. ---
    const bldgs = struct.buildings;
    const nBldg = bldgs.length / BUILDING_STRIDE;
    // The authored frontier assemblies replace the old per-record buildings.
    // Compact/specialized plots retain the shared fitted program below.
    group.add(frontierBuildingsView(bldgs));
    if (nBldg > 0) {
      // R3 districts: a subtle per-district palette shift over the age tint —
      // sections of a town read as coherent quarters. Derived (districtAt), so
      // renderer and any future consumer agree with zero serialization. Hoisted
      // above the path split: BOTH building pipelines multiply their wall
      // colours by the same districtTintFor(b), so quarters read identically
      // whichever path is active.
      const DISTRICT_TINTS = [
        [1.06, 1.0, 0.94], [0.97, 1.0, 1.05], [1.03, 1.03, 0.97], [0.95, 0.98, 1.0],   // core sectors
        [1.04, 0.99, 0.95], [0.96, 1.01, 1.03], [1.0, 1.03, 0.95], [0.97, 0.97, 1.02], // mid
        [1.02, 0.98, 0.96], [0.96, 1.0, 1.02], [1.0, 1.01, 0.97], [0.98, 0.98, 1.0],   // outer
      ];
      const seedNodes: { u: number; v: number }[] = [];
      for (let i = 0; i < g.nodeKind.length; i++) {
        if (g.nodeKind[i] === NODE_SEED) seedNodes.push({ u: g.nodeU[i], v: g.nodeV[i] });
      }
      const planetHashD = hashSeed(planet.sampler.def.seed);
      const cellKeyD = packCell(g.regionId);
      const dUv: [number, number] = [0, 0];
      const dDir = new THREE.Vector3();
      const districtTintFor = (b: number): [number, number, number] => {
        if (g.tier < 3) return [1, 1, 1];
        dDir.set(bldgs[b * BUILDING_STRIDE], bldgs[b * BUILDING_STRIDE + 1], bldgs[b * BUILDING_STRIDE + 2]).normalize();
        dirToLocal(g.frame, dDir.x, dDir.y, dDir.z, dUv);
        const d = districtAt(planetHashD, cellKeyD, seedNodes, g.tier, dUv[0], dUv[1]);
        return DISTRICT_TINTS[d.id] as [number, number, number];
      };
      {
        // ── NEW procedural path (flag ON): pure kit-doc expansion → instanced
        //    bake. buildingInstances bakes kit palette × archetype ramp × age
        //    tint into each instance colour; the district tint is a RENDERER
        //    concern (needs the region graph), so multiply it in here per
        //    building before the bake reads the colours. Glass instance colours
        //    are multiplied too — harmless, the night pass drives the shared
        //    glass mat colour every frame. ──
        const instList = buildingInstances(bldgs);
        for (const bi of instList) {
          const dt = districtTintFor(bi.b);
          bi.color[0] *= dt[0]; bi.color[1] *= dt[1]; bi.color[2] *= dt[2];
        }
        const { meshes, glassMats, wallMat } = bakeBuildingMeshes(instList);
        for (const m of meshes) {
          m.castShadow = true;
          m.receiveShadow = true;
          group.add(m);
        }
        // Auto-doors (2026-07-30): record this region's doorways for the
        // proximity-animated leaf pool.
        v.doors = buildingDoors(bldgs);
        // Night-poke wiring: the update() pass gates on v.buildingMat !== null,
        // pokes its emissive (warm wall wash) and v.windowMat.color (lamplight).
        // wallMat is the bake's ONE shared wall Lambert, glassMats[0] its ONE
        // shared window MeshBasic — so both pokes keep working unchanged.
        v.buildingMat = wallMat;
        v.windowMat = glassMats[0] ?? null;
      }
    }

    // --- Streetlights (Task ST-2): instanced posts + heads, always visible. ---
    // Seeded lamp placement (shared, deterministic) → two InstancedMeshes. The
    // head material is stored on v so the night pass pokes its emissive warm
    // (ST-3); the planet-local lamp bases are cached for the nearest-N real
    // PointLight tracker (ST-4).
    {
      const lamps = buildStreetlights(g, planet.sampler);
      const slb = bakeStreetlights(lamps);
      if (slb) {
        slb.posts.castShadow = true;
        slb.posts.receiveShadow = true;
        group.add(slb.posts, slb.heads);
        v.lampMat = slb.headMat;
        const nLamp = lamps.length / STREETLIGHT_STRIDE;
        const positions = new Float32Array(nLamp * 3);
        for (let i = 0; i < nLamp; i++) {
          const o = i * STREETLIGHT_STRIDE;
          positions[i * 3] = lamps[o];
          positions[i * 3 + 1] = lamps[o + 1];
          positions[i * 3 + 2] = lamps[o + 2];
        }
        v.lampPositions = positions;
      }
    }

    // Settlement street trees (2026-07-31): biome-matched trees along the
    // roads — sites are off-road + off-parcel by construction (worldgen), so
    // they can never stand in a building or a lane.
    {
      const treeSites = buildSettlementTrees(g, planet.sampler);
      for (const tree of planet.buildSettlementTreeMeshes(treeSites)) group.add(tree);
    }

    // ── LANDMARKS (2026-07-31): fountains on plazas, wells, monuments, and
    // park furniture on the hash-picked open dwelling parcels. Primitive
    // low-poly builds (the quest-kiosk idiom); park trees ride the
    // biome-matched settlement-tree builder.
    {
      const planetHashL = hashSeed(planet.sampler.def.seed);
      const rkL = packCell(g.regionId);
      const cylGeo = new THREE.CylinderGeometry(1, 1, 1, 10);
      const stoneMat = new THREE.MeshLambertMaterial({ color: 0x8f8a80 });
      const stoneDark = new THREE.MeshLambertMaterial({ color: 0x6c675e });
      const poolMat = makePoolWaterMat(WATER_TIME);
      const woodMat = new THREE.MeshLambertMaterial({ color: 0x6b4a2a });
      const parkTreeSites: number[] = [];
      const dL: number[] = [0, 0, 0];
      const basePost = (b: THREE.Vector3, axis: THREE.Vector3, off: number): THREE.Vector3 =>
        b.clone().addScaledVector(axis, off);
      const put = (geo: THREE.BufferGeometry, mat: THREE.Material,
        base: THREE.Vector3, up: THREE.Vector3, q: THREE.Quaternion,
        lift: number, sx: number, sy: number, sz: number): void => {
        const m = new THREE.Mesh(geo, mat);
        m.position.copy(base).addScaledVector(up, lift);
        m.quaternion.copy(q);
        m.scale.set(sx, sy, sz);
        m.castShadow = true; m.receiveShadow = true;
        group.add(m);
      };
      // WATER put: no shadow casting (water reads wrong as a shadow caster), and
      // an optional TILT — tilt>0 leans the mesh's top toward the tangent
      // direction `lean` by tilt radians (negative leans away).
      const putW = (geo: THREE.BufferGeometry, mat: THREE.Material,
        base: THREE.Vector3, up: THREE.Vector3, q: THREE.Quaternion,
        lift: number, sx: number, sy: number, sz: number,
        lean?: THREE.Vector3, tilt = 0): void => {
        const m = new THREE.Mesh(geo, mat);
        m.position.copy(base).addScaledVector(up, lift);
        m.quaternion.copy(q);
        if (lean && tilt !== 0) {
          const axis = new THREE.Vector3().crossVectors(lean, up).normalize();
          m.quaternion.premultiply(new THREE.Quaternion().setFromAxisAngle(axis, -tilt));
        }
        m.scale.set(sx, sy, sz);
        m.castShadow = false; m.receiveShadow = true;
        group.add(m);
      };
      for (let pi2 = 0; pi2 < g.parcels.length; pi2++) {
        const parcel = g.parcels[pi2];
        const use = parcel.use;
        const park = isParkParcel(planetHashL, rkL, pi2, use);
        if (use !== USE_MARKET_SQ && use !== USE_WELL && use !== USE_MONUMENT
          && use !== USE_LANDING_PAD && !park) continue;
        const poly = parcel.poly;
        let cu = 0, cv = 0;
        for (let i = 0; i < poly.length; i += 2) { cu += poly[i]; cv += poly[i + 1]; }
        const nCorner = poly.length / 2;
        if (nCorner === 0) continue;
        cu /= nCorner; cv /= nCorner;
        localToDir(g.frame, cu, cv, dL);
        // Sloped plazas: the centroid's surface can sit BELOW the uphill edge,
        // burying half the basin (2026-08-02 probe finding). Ground the build
        // on the HIGHEST of centre + an 8-point ring at the biggest basin
        // radius (~6 m — the first 4-sample/5 m cut still buried a big basin).
        let surface = planet.sampler.surfaceRadius(dL[0], dL[1], dL[2]);
        {
          const dS: number[] = [0, 0, 0];
          for (let rs = 0; rs < 8; rs++) {
            const a = (rs / 8) * Math.PI * 2;
            localToDir(g.frame, cu + Math.cos(a) * 6, cv + Math.sin(a) * 6, dS);
            const sr = planet.sampler.surfaceRadius(dS[0], dS[1], dS[2]);
            if (sr > surface) surface = sr;
          }
        }
        yA.set(dL[0], dL[1], dL[2]).normalize();
        xA.set(...g.frame.t1).addScaledVector(yA, -xA.dot(yA)).normalize();
        zA.crossVectors(xA, yA).normalize();
        basis.makeBasis(xA, yA, zA);
        quat.setFromRotationMatrix(basis);
        const base = new THREE.Vector3(dL[0], dL[1], dL[2]).multiplyScalar(surface);
        if (use === USE_MARKET_SQ) {
          const fh = hashInts(planetHashL ^ 0x4f21, rkL, pi2, 0);
          const fountain = buildTownFountain(fh, WATER_TIME);
          fountain.group.position.copy(base);
          fountain.group.quaternion.copy(quat);
          group.add(fountain.group);
        } else if (use === USE_LANDING_PAD) {
          // AUTHORED LANDING PAD (2026-08-02 "pads are just generic buildings"):
          // the parcel is OPEN now (structures stamps no box) — an octagonal
          // apron with a painted touchdown ring, corner marker lights and a
          // beacon mast. Low profile (≤0.3 m) so it stays walkable without a
          // dedicated collider, like the fountain basins.
          const padGeo = new THREE.CylinderGeometry(1, 1, 1, 8);
          const ringMat = new THREE.MeshLambertMaterial({ color: 0xd8b04a, emissive: 0x4a3a12 });
          const lightMat = new THREE.MeshLambertMaterial({ color: 0x223338, emissive: 0x35e0ff });
          put(padGeo, stoneMat, base, yA, quat, 0.12, 6.2, 0.24, 6.2);
          put(padGeo, stoneDark, base, yA, quat, 0.25, 4.6, 0.06, 4.6);
          put(cylGeo, ringMat, base, yA, quat, 0.29, 3.4, 0.05, 3.4);
          put(cylGeo, stoneDark, base, yA, quat, 0.32, 2.6, 0.05, 2.6);
          for (let j = 0; j < 4; j++) {
            const a = (j / 4) * Math.PI * 2 + Math.PI / 4;
            const off = new THREE.Vector3().addScaledVector(xA, Math.cos(a)).addScaledVector(zA, Math.sin(a));
            put(kitBox(), lightMat, basePost(base, off, 5.4), yA, quat, 0.5, 0.22, 0.55, 0.22);
          }
          put(cylGeo, stoneDark, basePost(base, xA, 6.7), yA, quat, 1.6, 0.12, 3.2, 0.12);
          put(kitBox(), lightMat, basePost(base, xA, 6.7), yA, quat, 3.35, 0.3, 0.32, 0.3);
        } else if (use === USE_WELL) {
          // WELL: stone ring + posts + little cone roof (pool water now animated).
          put(basinGeometry(.78, .24), stoneMat, base, yA, quat, 0, 1, 1, 1);
          const wellSurface = new THREE.CircleGeometry(.765, 32).rotateX(-Math.PI / 2);
          putW(wellSurface, poolMat, base, yA, quat, .58, 1, 1, 1);
          put(kitBox(), woodMat, basePost(base, xA, 0.85), yA, quat, 0.95, 0.14, 1.9, 0.14);
          put(kitBox(), woodMat, basePost(base, xA, -0.85), yA, quat, 0.95, 0.14, 1.9, 0.14);
          put(kitRoofCone(), stoneDark, base, yA, quat, 2.1, 1.5, 0.6, 1.5);
        } else if (use === USE_MONUMENT) {
          if(g.civics?.some(c=>c.ward.parcel===pi2))continue;
          // MONUMENT: plinth + obelisk (variant by hash: tall spire / twin slab).
          const variant = hashInts(planetHashL ^ 0x51ee, rkL, pi2, 3) & 1;
          put(kitBox(), stoneMat, base, yA, quat, 0.35, 2.2, 0.7, 2.2);
          if (variant === 0) {
            put(kitBox(), stoneDark, base, yA, quat, 2.1, 0.9, 2.8, 0.9);
            put(kitBox(), stoneMat, base, yA, quat, 4.0, 0.5, 1.1, 0.5);
          } else {
            put(kitBox(), stoneDark, basePost(base, xA, 0.55), yA, quat, 1.9, 0.7, 2.4, 0.5);
            put(kitBox(), stoneDark, basePost(base, xA, -0.55), yA, quat, 1.6, 0.7, 1.8, 0.5);
          }
        } else if (park) {
          // PARK: benches + biome trees on the open parcel.
          for (let bIdx = 0; bIdx < 2; bIdx++) {
            const off = bIdx === 0 ? 2.2 : -2.4;
            put(kitBox(), woodMat, basePost(base, zA, off), yA, quat, 0.5, 1.7, 0.09, 0.45);
            put(kitBox(), woodMat, basePost(base, zA, off), yA, quat, 0.25, 1.5, 0.42, 0.32);
          }
          // 2026-08-02 density+scale pass: parks carry a real stand (5..8
          // trees) at FOREST scale — the old 0.9..1.4 roll rendered the same
          // species at half size, which read as a different tree entirely.
          const nT = 5 + (hashInts(planetHashL ^ 0x7133, rkL, pi2, 5) % 4);
          for (let t = 0; t < nT; t++) {
            const hs = hashInts(planetHashL ^ 0x7134, rkL, pi2, t);
            const au = cu + (h01(hs) * 2 - 1) * 8;
            const av = cv + (h01(hs ^ 0x9e37) * 2 - 1) * 8;
            // A small parcel's ±8 m spread can spill onto the street — park
            // trees stay INSIDE their parcel (roads never cross parcels).
            if (!pointInPolygon(poly, au, av)) continue;
            localToDir(g.frame, au, av, dL);
            const hR = planet.sampler.def.radius + planet.sampler.height(dL[0], dL[1], dL[2]);
            parkTreeSites.push(dL[0] * hR, dL[1] * hR, dL[2] * hR,
              h01(hs ^ 0x77aa), 1.35 + h01(hs ^ 0x1234) * 1.0, h01(hs ^ 0xabcd) * Math.PI * 2);
          }
        }
      }
      if (parkTreeSites.length > 0) {
        for (const tree of planet.buildSettlementTreeMeshes(Float64Array.from(parkTreeSites))) group.add(tree);
      }
    }

    // Quest-board KIOSK REMOVED (2026-08-02 user call): the overhauled plaza
    // fountains stand on the same market-square centre the kiosk occupied.
    // The board DATA and its analytic chart coordinate live on — nearestBoard,
    // the HUD prompt and the server proximity gate all keep working; the
    // fountain itself is now the physical landmark you read the board at.

    // Built INVISIBLE: the main loop pre-warms its shaders (compileAsync) and
    // reveals it once ready, so first-visit geometry never triggers a synchronous
    // shader-compile / buffer-upload stall in the reflection + draw passes.
    group.visible = false;
    planet.group.add(group);
    v.group = group;
    this.pendingWarm.push(group);
  }

  /**
   * Drain the groups built this frame that still need a shader pre-warm. The main
   * loop calls `renderer.compileAsync(group, camera, scene)` on each (off-thread
   * parallel compile) and sets `group.visible = true` when it resolves. Returns a
   * fresh array each call (the internal queue is cleared).
   */
  takePendingWarm(): THREE.Group[] {
    if (this.pendingWarm.length === 0) return [];
    const w = this.pendingWarm;
    this.pendingWarm = [];
    return w;
  }

  private disposeVis(v: SettlementVis): void {
    if (!v.group) return;
    v.group.parent?.remove(v.group);
    const lampGeoms = streetlightUnitGeoms();
    v.group.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        if (o instanceof THREE.InstancedMesh) o.dispose();
        // Unit kit geometries (box + roof cone), the streetlight post/head
        // singletons AND flora-library tree geometry (sharedGeometry tag) are
        // shared module geometries — dispose only per-settlement geometry, or
        // a teardown frees geometry live settlements/forests still use.
        if (o.userData.sharedGeometry !== true
          && o.geometry !== unitBox && o.geometry !== unitRoofCone
          && o.geometry !== lampGeoms.post && o.geometry !== lampGeoms.head) {
          o.geometry.dispose();
        }
        const mat = o.material as THREE.Material | THREE.Material[];
        if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
        else mat.dispose();
      }
    });
    v.lampMat = null;
    v.lampPositions = null;
    v.group = null;
  }

  dispose(): void {
    for (const v of this.vis.values()) this.disposeVis(v);
    this.vis.clear();
    this.empty.clear();
    // The lamp pool lives on the scene root (or a planet group) for its whole
    // life — unparent it so a reconstructed renderer's fresh pool doesn't stack.
    for (const l of this.lampLights) l.removeFromParent();
  }
}
